import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Stack, Typography, Chip, Card, IconButton, Tooltip,
  ToggleButton, ToggleButtonGroup, useTheme,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Switch, FormControlLabel, Divider
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import VisibilityRounded from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRounded from "@mui/icons-material/VisibilityOffRounded";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import CalendarMonthRounded from "@mui/icons-material/CalendarMonthRounded";
import AlignHorizontalLeftRounded from "@mui/icons-material/AlignHorizontalLeftRounded";
import CloseIcon from "@mui/icons-material/Close";
import { LineChart } from "@mui/x-charts/LineChart";
import { ScatterChart } from "@mui/x-charts/ScatterChart";
import { ResponsiveChartContainer } from "@mui/x-charts/ResponsiveChartContainer";
import { useAxisTooltip } from "@mui/x-charts/ChartsTooltip";
import { ChartsAxisHighlight } from "@mui/x-charts/ChartsAxisHighlight";
import { ChartsReferenceLine } from "@mui/x-charts/ChartsReferenceLine";
import {
  LinePlot, MarkPlot, AreaPlot, LineHighlightPlot
} from "@mui/x-charts/LineChart";
import { ScatterPlot } from "@mui/x-charts/ScatterChart";
import type { BrokerAccount, Cashflow } from "@/data/mock-data";
import { TERMS } from "@/lib/terms";
import { MetricTip } from "@/components/MetricTip";
import {
  computeSMA,
  resolveSmaWindow,
  rebaseCumulativePct,
  type SmaRangeKey
} from "@/lib/chart-math";
import {
  useChartSettingsStore,
  SMA_PRESETS,
  type ChartMode,
  type LineKey
} from "@/store/chart-settings-store";

/**
 * ⚠️  LOCKED 2026-04-19 (R12-10 bump) — Graph is finished.
 *
 *   All line formulas, anchor rules, rebase semantics, cashflow dot
 *   positioning, and hero-hover math are FROZEN. The canonical contract
 *   lives in `docs/CHART-SPEC.md`. Do NOT change the math in this file
 *   without updating that spec first AND bumping its "Status / Last
 *   locked" date.
 *
 *   This chart is the reference implementation for Bots and any future
 *   ticker-analysis / comparison view. Before porting, read the "Reusing
 *   this chart" section of the spec — don't copy-paste; refactor toward
 *   the shared `{ primary, benchmarks[], target?, netDeposits? }` config
 *   described there.
 *
 *   Inception-anchor invariant (MANDATORY): every % and $ line is
 *   computed across the FULL history first, then sliced to the active
 *   window. A given calendar date must read the same value regardless of
 *   which range chip is active. Rebase is the only post-slice offset.
 */

/**
 * Portfolio Graph — Portfolio · SPY · Target comparison chart.
 *
 * Round 7 (2026-04-17 v7, per Sergey's feedback):
 *  • Floating popup tooltip is GONE. The legend chips (P, SPY, T) now
 *    act as the tooltip: as the crosshair moves, their values update
 *    live to show the series value at that x-index.
 *  • A single, centered date label sits below the chip row — the only
 *    "tooltip" that remains. Empty space reserved (nbsp) when not
 *    hovering so the layout doesn't jump.
 *  • Implemented via a tiny `HoverTracker` component nested inside
 *    `ResponsiveChartContainer` that calls `useAxisTooltip()` and syncs
 *    the x-index up to the outer component through a stable setter.
 *    ChartsTooltip / AxisTooltipContent / tooltipAnchor machinery is
 *    removed — the axis interaction state is already populated by
 *    `ChartsSurface` (via `useAxisEvents`), so we don't need the
 *    popper-rendered tooltip at all.
 *  • Synthetic "initial deposit" dot at x=0 so the first day of every
 *    window has a cashflow marker representing the starting balance.
 *
 * Round 6 (2026-04-17 v6): fix dim active range chip.
 * Round 5 (2026-04-17 v5): sticky account selector + pin tooltip above chart.
 * Round 4 (2026-04-17 v4): (i) persists, tooltip series rows, custom date range.
 * Round 3 (2026-04-17 v3): hollow donut hover dots, cashflow tooltip rows,
 *  Target gear icon, (i) next to title.
 */

export interface PortfolioVsSpyChartProps {
  account: BrokerAccount;
  /** Compact layout for mobile — shorter chart + abbreviated chip labels. */
  compact?: boolean;
  /**
   * V2 (Robinhood-style) layout. Differences vs V1:
   *  • Header row (title + (i) + $/% toggle + eye) is gone — the page
   *    owns the total-portfolio-value heading above us.
   *  • The Portfolio "P" legend chip is removed — the parent page shows
   *    the % change chip beside the total portfolio value instead, so
   *    having a duplicate on the chart is noise.
   *  • $/% toggle moves INTO the range chip row. The hide-chart eye
   *    moves to the END of the legend-chip row (next to SPY/T/ND) to
   *    keep the range row tight (it was wrapping otherwise).
   *  • Cashflow legend chip is removed — the cashflow amount appears
   *    inline with the date label instead, and ONLY when the crosshair
   *    sits on a non-zero cashflow. The cashflow DOTS on the chart are
   *    always visible (no toggle).
   *  • Y-axis is clamped tight to the visible series' min/max (with a
   *    small pad) so the lines fill the vertical space — no dead air
   *    above and below.
   *  • Chart height grows (the page has no AppBar so there's room) to
   *    fill the viewport better.
   *  • Range presets include "W" (7 days) in addition to M/3M/6M/YTD/1Y/All.
   */
  v2?: boolean;
  /**
   * Fires whenever the crosshair position changes. `null` = the cursor
   * is off the chart. Consumers (V2 AccountsPage) use this to make the
   * hero "Total Portfolio" value + delta react to the hover position.
   *
   * `usd` is the raw portfolio $ value at the hovered index. `pctChange`
   * / `usdChange` are relative to the FIRST visible x-index — so they
   * reflect "change within the selected range" (W / 1M / …). When the
   * range is ALL this is equivalent to all-time change.
   */
  onHoverState?: (state: null | {
    idx: number;
    usd: number;
    pctChange: number;
    usdChange: number;
    dateLabel: string;
    /**
     * True when the state reflects the user's crosshair position.
     * False when it reflects the *resting* window-end value — i.e.
     * `activeIdx = last idx in window`, per CHART-SPEC's
     * `activeIdx = hoverIdx ?? last` rule. Consumers that need to
     * render a "hover date" vs. a range label can read this flag.
     */
    isHover: boolean;
  }) => void;
  /**
   * Fires when the active range changes (preset or custom). Lets the
   * parent hero render the Robinhood-style "Past week" / "All time" /
   * "in range" label after the $ number when the user is NOT hovering.
   *
   *   key       — the RangeKey the chart is now on
   *   label     — human-readable label ("Past week", "All time", "in range", …)
   *   customLabel — for CUSTOM only, the formatted "Apr 3 – Jul 17" string
   *                (null for presets) so the parent can reuse it if it
   *                wants to show the concrete window alongside.
   */
  onRangeChange?: (info: {
    key: RangeKey;
    label: string;
    customLabel: string | null;
    /** ISO yyyy-mm-dd of the active window's first day. Lets the
     *  parent's range-scoped metrics card compute window-bounded
     *  return / APY / Max DD / Sharpe without re-implementing the
     *  range resolution that lives in `sliceByRange`. Empty string
     *  while the chart is hydrating (`sliced.dates.length === 0`). */
    startIso: string;
    /** ISO yyyy-mm-dd of the active window's last day. Same lifecycle
     *  + caveats as `startIso`. */
    endIso: string;
  }) => void;
  /**
   * Optional *controlled* graph mode. When provided, the chart reads
   * which axis ($ vs %) to show from this prop and reports changes via
   * `onModeChange`. When omitted, the chart keeps its own internal mode
   * state (V1 behaviour). V2 uses the controlled form so the parent hero
   * can drive mode switching by tapping the $/% delta chips above the
   * chart.
   */
  mode?: GraphMode;
  onModeChange?: (mode: GraphMode) => void;
}

export type GraphMode = "pct" | "usd";
type RangeKey = "W" | "1M" | "3M" | "6M" | "YTD" | "1Y" | "ALL" | "CUSTOM";
/**
 * Preset ordering used for V1 (no W). V2 prepends "W" via a separate
 * array — keeps V1 chip order untouched so existing screenshots match.
 */
const PRESET_RANGES: Exclude<RangeKey, "CUSTOM">[] = [
  "1M", "3M", "6M", "YTD", "1Y", "ALL"
];
const PRESET_RANGES_V2: Exclude<RangeKey, "CUSTOM">[] = [
  "W", "1M", "3M", "6M", "YTD", "1Y", "ALL"
];
type Mode = GraphMode;
interface CustomRange { from: string; to: string }

/**
 * Human-readable label for a range key, in Robinhood's idiom. Drives the
 * hero's "Past week" / "All time" / "in range" tagline that sits after
 * the $ number. Intentionally NOT "1W" / "1M" — the chip rail already
 * shows those abbreviations; the hero uses full prose so it reads as a
 * sentence completion ("$9,860.69 · Past week").
 */
function rangeLabel(key: RangeKey): string {
  switch (key) {
    case "W":      return "Past week";
    case "1M":     return "Past month";
    case "3M":     return "Past 3 months";
    case "6M":     return "Past 6 months";
    case "YTD":    return "Year to date";
    case "1Y":     return "Past year";
    case "ALL":    return "All time";
    case "CUSTOM": return "in range";
  }
}

/** Chart line colors. */
const COLORS = {
  portfolio:  "#a78bfa",      // violet
  spy:        "#f97316",      // orange
  spyDiv:     "#fbbf24",      // amber — SPY-family, lighter, clearly distinct
  target:     "#10b981",      // emerald
  deposit:    "#22d3ee",      // cyan
  withdrawal: "#f87171",      // red
  netDeposit: "#94a3b8",      // slate — neutral derived stat, no chart line
  /** SMA — soft sky blue. Distinct enough from Portfolio violet that
   *  the smoothed trend reads as a separate overlay rather than a faded
   *  duplicate of the live line; out of every other line family
   *  (orange SPY, amber SPY+D, emerald Target, slate ND, cyan/red
   *  cashflow dots) too. Sergey 2026-04-30: previous violet variant
   *  was too close to the Portfolio colour. */
  sma:        "#7dd3fc"
};

/**
 * Base style for every range chip. Font size matches the "Buying Power"
 * row label (body1 = 1rem) per Apr-2026 usability pass — clickable range
 * presets were smaller than the rest of the clickable text on the page,
 * which made them feel like secondary controls even though they drive
 * the chart. Height bumped proportionally so the padding around the
 * label stays visually balanced.
 */
const activeRangeChipSx = { fontWeight: 600, fontSize: "1rem", height: 36 };

/**
 * Active-range override. The global theme (`MuiChip.styleOverrides.filled`
 * in theme/components.ts) forces every filled chip's background to
 * `#16213a` regardless of its `color` prop — which leaves the active
 * chip with the theme's `primary.contrastText` (a very dark green)
 * rendered on dark navy, making the label look dim. We force the
 * filled+primary combo here so the selected range is unambiguously
 * "primary mint with black text".
 */
const activeRangeChipActiveSx = {
  bgcolor: "primary.main",
  color: "primary.contrastText",
  "&:hover": { bgcolor: "primary.light" },
  "& .MuiChip-deleteIcon": {
    color: "primary.contrastText",
    opacity: 0.8,
    "&:hover": { color: "primary.contrastText", opacity: 1 }
  }
};

/**
 * Series margin used by the chart. MUST stay in sync with the
 * `margin` prop on `ResponsiveChartContainer` below — every pointer→
 * index calculation maps client-x through this inset to reach the same
 * plot area MUI uses internally.
 *
 * Sized so the crosshair marker at the FIRST / LAST point is fully
 * visible (Sergey 2026-08-03). The marker is `r=5` + `strokeWidth=2`,
 * i.e. 6px of outer radius; with a point scale the endpoints sit
 * exactly ON the drawing-area edge, so anything less than ~6px of
 * margin renders them as clipped half-moons at the card boundary.
 * 16px leaves the dot fully drawn with ~10px of breathing room.
 *
 * History: this was 6 → 8 → 10, each bump chasing the same clipped
 * endpoint. None of them worked because the chart wrapper ALSO carried
 * `mx: -1` (an extra −8px full-bleed pull) on top of the page's own
 * `mx: -2`, which put the SVG's left edge 8px OUTSIDE the visible card
 * — so a 10px margin still left the endpoint only 2px inside the card
 * and the dot 4px past it. The pull is gone now (see the wrapper Box)
 * and this margin is measured from the real card edge.
 */
const CHART_MARGIN_PX = 16;

/**
 * Minimum grab distance for the first / last point, in px. See
 * `pointerIndex` — a fat-finger / imprecise-mouse allowance so the
 * endpoints stay selectable on dense ranges.
 */
const EDGE_GRAB_MOUSE_PX = 14;
const EDGE_GRAB_TOUCH_PX = 24;

/**
 * Map a pointer's client-x onto a categorical x-axis index, with an
 * edge-snap so the FIRST and LAST points are always reachable.
 *
 * Why the snap exists (Sergey 2026-08-03): with `scaleType: "point"`
 * MUI positions index 0 exactly on the left edge of the drawing area
 * and index N-1 exactly on the right edge, and its hit-test
 * (`useAxisEvents` → `isPointInside`) rejects anything outside that
 * rect. Each point's natural hit zone is therefore half a step wide —
 * fine when the range is sparse, impossible when it isn't:
 *
 *     range   points   step      endpoint hit zone
 *     W            5   170px     85px
 *     1M          22    32px     16px
 *     3M          65    11px      5px
 *     1Y         250   2.7px    1.4px
 *     ALL        500   1.4px    0.7px
 *
 * At 1Y / ALL the first and last values were sub-pixel targets — you
 * could never hover or tap them, which is exactly what was reported.
 *
 * The band is `max(step / 2, grab)`: for sparse ranges `step / 2` wins
 * and behaviour is identical to plain nearest-point rounding (no
 * stealing from index 1); for dense ranges the grab distance wins and
 * the endpoint becomes reachable. Interior indices are unaffected —
 * they're sub-pixel there anyway and can't be individually targeted by
 * a human regardless.
 *
 * Returns `null` when the geometry isn't measurable yet.
 */
function pointerIndex(
  clientX: number,
  rect: DOMRect,
  len: number,
  pointerType: string,
  /** When true, only return an index inside the edge bands (used by
   *  the hover fallback so MUI keeps driving the chart interior). */
  edgesOnly = false
): number | null {
  if (len <= 0 || rect.width <= 0) return null;
  const last = len - 1;
  if (last === 0) return 0;

  const plotLeft = rect.left + CHART_MARGIN_PX;
  const plotWidth = rect.width - CHART_MARGIN_PX * 2;
  if (plotWidth <= 0) return null;
  const plotRight = plotLeft + plotWidth;

  const step = plotWidth / last;
  const grab =
    pointerType === "touch" ? EDGE_GRAB_TOUCH_PX : EDGE_GRAB_MOUSE_PX;
  const band = Math.max(step / 2, grab);

  if (clientX <= plotLeft + band) return 0;
  if (clientX >= plotRight - band) return last;
  if (edgesOnly) return null;

  const pct = Math.max(0, Math.min(1, (clientX - plotLeft) / plotWidth));
  return Math.max(0, Math.min(last, Math.round(pct * last)));
}

interface Sliced {
  dates: string[];
  twr: number[];
  spy: number[];
  /**
   * SPY total-return series ("ADJ"-style) — price + reinvested
   * dividends, rebased to 0 % at the first index of the FULL account
   * curve. Sliced/rebased the same way as `spy` so callers can treat
   * the two series interchangeably.
   */
  spyTotal: number[];
  usd: number[];
  startIdx: number;
}

function sliceByRange(
  dates: string[],
  twr: number[],
  spy: number[],
  spyTotal: number[],
  usd: number[],
  range: RangeKey,
  customRange: CustomRange | null
): Sliced {
  const n = dates.length;

  // CUSTOM: slice by the user's picked [from, to] ISO date strings.
  if (range === "CUSTOM" && customRange) {
    let startIdx = dates.findIndex((d) => d >= customRange.from);
    if (startIdx < 0) startIdx = 0;
    let endIdx = -1;
    for (let i = dates.length - 1; i >= 0; i--) {
      if (dates[i]! <= customRange.to) { endIdx = i; break; }
    }
    if (endIdx < 0) endIdx = dates.length - 1;
    if (endIdx < startIdx) { endIdx = startIdx; }
    return {
      dates: dates.slice(startIdx, endIdx + 1),
      twr: twr.slice(startIdx, endIdx + 1),
      spy: spy.slice(startIdx, endIdx + 1),
      spyTotal: spyTotal.slice(startIdx, endIdx + 1),
      usd: usd.slice(startIdx, endIdx + 1),
      startIdx
    };
  }

  // Slice by CALENDAR date boundary — not by data-point count.
  //
  // Real-account `curveDates` are trading-day-filtered (~252 entries /
  // calendar year, weekends + US holidays stripped). A naïve
  // `keep = 365` on a trading-day series would actually span ~1.45
  // calendar years, which corrupted every window-relative metric
  // (e.g. 1Y Target at 20 %/yr read ~30 % because the window was 1.45
  // years long). Mock accounts use calendar-daily curves so the old
  // count-based slice happened to work for those; switching to a date
  // boundary unifies both.
  const today = new Date();
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  let fromIso: string | null = null;
  if (range === "ALL") {
    fromIso = null;
  } else if (range === "YTD") {
    fromIso = new Date(today.getFullYear(), 0, 1).toISOString().slice(0, 10);
  } else {
    // Calendar-day offset from "today" (UTC). Uses setUTCDate so DST
    // transitions don't nudge the boundary by an hour and flip a day.
    const daysBack =
      range === "W" ? 7 :
      range === "1M" ? 30 :
      range === "3M" ? 90 :
      range === "6M" ? 180 :
      range === "1Y" ? 365 :
      0;
    const from = new Date(today);
    from.setUTCDate(from.getUTCDate() - daysBack);
    fromIso = iso(from);
  }
  let start = 0;
  if (fromIso !== null) {
    const idx = dates.findIndex((d) => d >= fromIso!);
    start = idx < 0 ? n : idx;
  }
  if (start >= n) start = Math.max(0, n - 1);
  return {
    dates: dates.slice(start),
    twr: twr.slice(start),
    spy: spy.slice(start),
    spyTotal: spyTotal.slice(start),
    usd: usd.slice(start),
    startIdx: start
  };
}

/**
 * Build the dollar-mode Target curve: compound the starting portfolio
 * value at `targetApyPct` per year, then add each deposit growing from
 * its own date (deposits boost the curve; withdrawals subtract).
 */
function buildUsdTargetCurve(
  dateObjs: Date[],
  startValue: number,
  targetApyPct: number,
  cashflowsInWindow: { at: Date; amount: number }[]
): number[] {
  const rate = targetApyPct / 100;
  return dateObjs.map((d) => {
    const yrs = (d.getTime() - dateObjs[0]!.getTime()) / (365 * 86400e3);
    let v = startValue * Math.pow(1 + rate, yrs);
    for (const cf of cashflowsInWindow) {
      if (d.getTime() >= cf.at.getTime()) {
        const ry = (d.getTime() - cf.at.getTime()) / (365 * 86400e3);
        v += cf.amount * Math.pow(1 + rate, ry);
      }
    }
    return +v.toFixed(2);
  });
}

export function PortfolioVsSpyChart({
  account,
  compact = false,
  v2 = false,
  onHoverState,
  onRangeChange,
  mode: propMode,
  onModeChange
}: PortfolioVsSpyChartProps) {
  const theme = useTheme();
  // Default range per Sergey 2026-04-23: "1M" is the most useful
  // starting view for most accounts — it fits on screen without
  // weekend/holiday clutter and shows recent trading activity without
  // being dominated by long-tail history.
  const [range, setRange] = useState<RangeKey>("1M");
  // Internal fallback mode — used when the parent doesn't pass a
  // controlled `mode` prop. V2 always passes one, so the internal state
  // is a no-op in that path; V1 leaves it alone and drives its own
  // $/% toggle via this state as before.
  const [internalMode, setInternalMode] = useState<Mode>("pct");
  const mode = propMode ?? internalMode;
  // Unified setter — updates the parent when controlled, else the local
  // fallback. Always called with a concrete Mode (the previous
  // functional-setState call site has been removed along with the inline
  // V2 $/% toggle).
  const setMode = (next: Mode) => {
    if (propMode !== undefined) {
      onModeChange?.(next);
    } else {
      setInternalMode(next);
    }
  };
  // ─── Chip/line visibility — persisted per mode in Zustand ───────
  // Two axes per comparison line:
  //   • `enabled` — chip AND line exist. Flipped by Graph Settings
  //     dialog switches. OFF removes the chip from the strip and the
  //     line from the chart.
  //   • `active`  — with the chip visible, whether the line is drawn.
  //     Flipped by clicking the chip. OFF leaves the chip in place
  //     (dimmed) but hides the line.
  // Final visibility = enabled && active. Settings are per-mode
  // (`pct`/`usd`) so % and $ graphs track independently, and persisted
  // to localStorage so user choices survive reloads/logouts.
  const chartMode: ChartMode = mode === "usd" ? "usd" : "pct";
  const modeSettings = useChartSettingsStore((s) => s[chartMode]);
  const setEnabledInStore = useChartSettingsStore((s) => s.setEnabled);
  const toggleActiveInStore = useChartSettingsStore((s) => s.toggleActive);
  const setAllInStore = useChartSettingsStore((s) => s.setAll);
  // R12-11 "Selected Range" display toggle. When ON, the centered
  // date-label slot below the chip row shows the active range label
  // ("Past year" / "All time" / custom range) while the user is NOT
  // hovering. Hovering a specific date always wins (the hover date is
  // the more specific read). Off by default so the slot stays quiet at
  // rest — users opt in via Graph Settings if they want the persistent
  // range reminder above the chart.
  const showSelectedRange = useChartSettingsStore((s) => s.showSelectedRange);
  const setShowSelectedRange = useChartSettingsStore((s) => s.setShowSelectedRange);
  const enabledSpy = modeSettings.spy.enabled;
  const activeSpy = modeSettings.spy.active;
  const enabledSpyDiv = modeSettings.spyDiv.enabled;
  const activeSpyDiv = modeSettings.spyDiv.active;
  const enabledTarget = modeSettings.target.enabled;
  const activeTarget = modeSettings.target.active;
  const enabledCashflow = modeSettings.cashflow.enabled;
  const activeCashflow = modeSettings.cashflow.active;
  const enabledNetDeposits = modeSettings.netDeposits.enabled;
  const activeNetDeposits = modeSettings.netDeposits.active;
  const enabledSma = modeSettings.sma.enabled;
  const activeSma = modeSettings.sma.active;
  const showSpy = enabledSpy && activeSpy;
  const showTarget = enabledTarget && activeTarget;
  const showCashflow = enabledCashflow && activeCashflow;
  const showNetDeposits = enabledNetDeposits && activeNetDeposits;
  const showSpyDiv = enabledSpyDiv && activeSpyDiv;
  const showSma = enabledSma && activeSma;
  // SMA window resolution — auto picks per range, fixed honours the
  // user's stored value. Sergey 2026-04-30. Falls back to 0 on W
  // range and on custom ranges shorter than the smallest preset
  // (5 days), in which case the line never renders.
  const smaWindowMode = useChartSettingsStore((s) => s.smaWindowMode);
  const smaWindowDays = useChartSettingsStore((s) => s.smaWindowDays);
  const setSmaWindow = useChartSettingsStore((s) => s.setSmaWindow);
  // Convenience setters threaded through the store — each call writes
  // to the current mode only, so a toggle in $ never leaks into %.
  const setEnabled = useCallback(
    (key: LineKey, v: boolean) => setEnabledInStore(chartMode, key, v),
    [chartMode, setEnabledInStore]
  );
  const toggleLine = useCallback(
    (key: LineKey) => toggleActiveInStore(chartMode, key),
    [chartMode, toggleActiveInStore]
  );
  // Graph Settings dialog (V2 only) — houses the chip-visibility
  // switches (SPY, Target, SPY+D) and the $/% mode toggle. Opened via
  // the gear icon in the range-chip row.
  const [settingsOpen, setSettingsOpen] = useState(false);
  // `hidden` collapses the whole card to just its title + show/hide
  // toggle. V1 keeps this state internal — the chart owns its eye
  // button in the header row. V2 never sets `hidden` itself (no eye
  // inside the chart); the V2 page instead gates whether the chart
  // renders at all, so in V2 this state stays at `false` for the
  // chart's lifetime.
  const [hidden, setHidden] = useState(false);
  // Local override of the account's saved target APY — lets users
  // re-tune the Target line via the gear icon on the T-chip without
  // round-tripping through Configure. `null` = use account's stored
  // value (the canonical source of truth).
  const [targetPctOverride, setTargetPctOverride] = useState<number | null>(null);
  // Custom date range — populated by the calendar icon + Dialog. When
  // non-null AND `range === "CUSTOM"`, overrides the preset slicer.
  const [customRange, setCustomRange] = useState<CustomRange | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Target APY editor dialog — replaces the old `window.prompt` flow.
  const [targetDialogOpen, setTargetDialogOpen] = useState(false);
  // SMA window picker — opens from the gear icon on the SMA chip.
  // Lets the user pick Auto, a preset (5/10/20/30/50/100/200), or a
  // custom integer window. Sergey 2026-04-30.
  const [smaDialogOpen, setSmaDialogOpen] = useState(false);
  // Rebase ("normalize") — shifts Portfolio + SPY in % mode so both
  // lines start at 0% at the LEFT edge of the visible window. The
  // Target line is already 0% at the start by construction (it's a
  // compounding-from-today curve), so rebasing leaves it alone.
  //
  // Why this matters: when a range chip slices the series to, say,
  // the last 30 days, Portfolio/SPY still carry their inception-to-
  // date cumulative %, so the lines don't start from a common point —
  // making "which one outperformed in this window" hard to read at a
  // glance. Rebase fixes that by subtracting each series' value at
  // the first visible x-index.
  //
  // Only active in % mode. In $ mode the portfolio line shows actual
  // dollar value, where "rebasing" doesn't have a meaningful analogue.
  //
  // Default: ON. Without rebasing, non-ALL ranges show every series'
  // inception-anchored value (e.g. Target 20% APY would read "+46%"
  // on a 1Y window because the account is ~2 years old). That is
  // confusing — users expect chips/lines on a 1Y range to reflect
  // what happened inside the 1Y, not inception-to-now. Rebase=true
  // shifts every series so window-open = 0 and the chip values
  // become "change over the selected range", which matches the
  // mental model of every broker-app chart. In ALL range rebase is
  // a no-op (window-open IS inception) so the default is safe
  // there too.
  const [rebased, setRebased] = useState(true);
  // Previously we forced rebase=true on every non-ALL range (treating
  // it as a derived flag: `rebased || range !== "ALL"`). That made the
  // toggle a silent no-op on non-ALL ranges — the user could tap it
  // and nothing changed. Per Sergey's Apr-2026 feedback ("I can't turn
  // off rebase, why did you turn it off?") we now honour the raw flag
  // everywhere; defaults are set so the out-of-the-box view is
  // window-rebased on 1M, and the user stays in control from there.
  //
  // `effectiveRebased` is kept as an alias so callers below read a
  // stable name — if we ever need to force-rebase for a specific
  // range again, this is the one place to put that logic.
  const effectiveRebased = rebased;

  // x-axis index currently under the crosshair (or `null` when the
  // cursor is not over the chart). Updated by `HoverTracker` below via
  // `useAxisTooltip()`. When non-null, the legend chip values + date
  // label below them reflect the values at that index; when null, chips
  // fall through to `lockedIdx`, then to end-of-period (see `activeIdx`
  // rule in CHART-SPEC).
  const [muiHoverIdx, setMuiHoverIdx] = useState<number | null>(null);
  // Edge-hover fallback (Sergey 2026-08-03). MUI's `useAxisTooltip()`
  // only reports an axis index while the pointer is inside the chart's
  // *drawing area* — the rect inset by `margin` (10px each side). With
  // `scaleType: "point"` the FIRST data point sits exactly on the left
  // edge of that rect and the LAST exactly on the right edge, so their
  // hover target is ~1px wide: one pixel further out and the pointer is
  // in the margin gutter where MUI reports nothing at all. Net effect:
  // the first and last values on the line were impossible to hover
  // (desktop) or land on (mobile) — no crosshair, no reactive chips, no
  // hero readout.
  //
  // Fix: a geometry-based fallback that ONLY engages in the outer edge
  // band and takes priority over MUI's value. In the chart interior
  // this stays `null` and MUI drives hover exactly as before, so the
  // precision of the mid-chart hover is untouched (an earlier attempt
  // that intercepted pointermove across the WHOLE chart broke interior
  // hover and had to be rolled back — hence the narrow scope here).
  const [edgeHoverIdx, setEdgeHoverIdx] = useState<number | null>(null);
  const hoverIdx = edgeHoverIdx ?? muiHoverIdx;
  // R12-12: Tap-to-lock crosshair. When non-null, the chart "pins" the
  // crosshair + reactive chip values + hero big-number to this index
  // even after the pointer leaves the chart. Lets mobile users inspect
  // a specific day's reading without having to keep their finger on
  // the chart. Cleared by:
  //   • range chip tap (including re-tapping the current range)
  //   • $/% mode change
  //   • account switch
  //   • × chip next to the centered date label
  //   • Escape key on desktop
  //   • tapping inside the card but outside the chart SVG
  // Lock wins over hover per the refined R12-12 rule. Once set (via
  // desktop click or mobile touch-release), moving the pointer no
  // longer shifts `activeIdx` — the crosshair + hero values stay
  // pinned until the user clears via × / Escape / range / mode /
  // account change. CHART-SPEC `activeIdx` rule is now:
  // `activeIdx = lockedIdx ?? hoverIdx ?? last`.
  const [lockedIdx, setLockedIdx] = useState<number | null>(null);
  // Mirror of the last *non-null* `hoverIdx`. Used by the mobile
  // auto-lock on pointerup: MUI clears its axis tooltip state (and
  // therefore our `hoverIdx`) on pointerup, so by the time the
  // pointerup handler fires, `hoverIdx` is already null. This ref
  // preserves the position the finger was at the instant before it
  // lifted, which is the date the user wants locked.
  const lastHoverIdxRef = useRef<number | null>(null);
  // Tracks whether we've seen a hover event inside the chart during
  // the *current* touch gesture. Cleared on each new pointerdown so
  // a stale pre-existing hoverIdx from an earlier touch can't leak
  // into the next auto-lock. Set to true whenever `hoverIdx`
  // transitions to a non-null value (i.e. the finger is over the
  // chart and MUI has reported an axis index). Only reads true ⇒
  // auto-lock on the subsequent pointerup.
  const hoverSeenInTouchRef = useRef<boolean>(false);
  // Edge-tap fallback (Sergey 2026-04-30): MUI's `useAxisTooltip()`
  // does not always fire for touches that land on the very first or
  // very last data point of the window — the pointer is in the 6px
  // gutter past the categorical scale's plot area, OR a cashflow
  // scatter dot intercepts the pointer event before MUI's axis hover
  // logic runs. Both cases leave `lastHoverIdxRef.current` stuck on
  // the second-to-last interior index, so the lock can never reach
  // index 0 or index N-1 via touch.
  //
  // Fix: capture every pointer's last known clientX + the chart
  // wrapper's bounding rect during pointerdown / pointermove. On
  // touch pointerup, if the hover ref is missing OR the captured x
  // sits in the outer 6% of the wrapper, snap geometrically to the
  // nearest categorical index using the same MARGIN math the desktop
  // pointerdown handler uses. Index 0 and N-1 become reliably
  // selectable on All / 1Y / 6M and any other range where they map
  // to the very edge of the plot.
  const chartWrapperRef = useRef<HTMLDivElement | null>(null);
  const lastTouchClientXRef = useRef<number | null>(null);
  useEffect(() => {
    if (hoverIdx != null) {
      lastHoverIdxRef.current = hoverIdx;
      hoverSeenInTouchRef.current = true;
    }
  }, [hoverIdx]);
  // Mobile auto-lock: window-level pointerup listener so we still
  // catch the release even if the finger drags off the chart before
  // lifting. Only fires for touch pointers, only when not already
  // locked, and only if the current touch gesture actually produced
  // a hover read (so a stray pointerup elsewhere on the page can't
  // accidentally pin a stale index).
  //
  // Note: we can't directly reference `dateObjs` here because this
  // block runs before the `useMemo` that derives it — `tsc -b` flags
  // the forward reference. Instead we use a ref that the dateObjs
  // declaration below keeps up-to-date, so the handler always reads
  // the current length without creating a TDZ violation.
  const dateObjsLenRef = useRef(0);
  useEffect(() => {
    const onUp = (e: PointerEvent) => {
      if (e.pointerType !== "touch") return;
      if (lockedIdx != null) return;
      const len = dateObjsLenRef.current;
      if (len === 0) return;

      // Primary path: MUI hover fired during this gesture, so we have
      // a live index from `lastHoverIdxRef`. This handles the common
      // mid-chart drag-and-release case.
      let idx: number | null = null;
      if (hoverSeenInTouchRef.current) {
        const ref = lastHoverIdxRef.current;
        if (ref != null && ref >= 0 && ref < len) idx = ref;
      }

      // Edge-tap fallback (Sergey 2026-04-30): if hover never fired
      // OR fired but landed on an interior index even though the
      // touch was at the very edge of the wrapper, snap geometrically
      // using the captured clientX + wrapper rect. This recovers the
      // first/last index (e.g. Apr 30 today on All / 1Y / 6M) when
      // MUI's axis hover doesn't reach the boundary.
      const wrapper = chartWrapperRef.current;
      const cx = lastTouchClientXRef.current ?? e.clientX;
      if (wrapper && Number.isFinite(cx)) {
        const rect = wrapper.getBoundingClientRect();
        if (rect.width > 0) {
          const geomIdx = pointerIndex(cx, rect, len, "touch");
          // `pointerIndex` with `edgesOnly` tells us whether the touch
          // landed in an edge band. When it did, geometry ALWAYS wins:
          // MUI's hover can't reach the endpoints on dense ranges, so a
          // finger near either end unambiguously means "first/last
          // point" even if hover reported some interior index.
          const inEdgeBand = pointerIndex(cx, rect, len, "touch", true);
          if (geomIdx != null && (idx == null || inEdgeBand != null)) {
            idx = geomIdx;
          }
        }
      }

      if (idx != null && idx >= 0 && idx < len) {
        setLockedIdx(idx);
      }
      hoverSeenInTouchRef.current = false;
      lastTouchClientXRef.current = null;
      // A finger lift doesn't fire pointerleave, so clear the edge-hover
      // fallback here — otherwise it would stay pinned to index 0 / N-1
      // after the touch ends.
      setEdgeHoverIdx(null);
    };
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [lockedIdx]);
  // R12-12: Clear the lock whenever the window changes shape under it.
  //   • range / customRange change: chip tap (including re-tapping the
  //     current range) flips one of these, so setLockedIdx(null) runs.
  //   • mode change ($ ↔ %): reset so the user doesn't stare at a
  //     crosshair that was placed under a different y-scale.
  //   • account change: indices are account-relative and meaningless
  //     across accounts (different dateObjs, different length).
  // Escape key is handled by a dedicated effect further below so it
  // can attach/detach a window listener.
  useEffect(() => {
    setLockedIdx(null);
  }, [range, customRange, mode, account.id]);
  // Escape key dismisses the lock on desktop — keyboards on mobile
  // don't expose Escape reliably, but it's a free win on desktop.
  useEffect(() => {
    if (lockedIdx == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLockedIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lockedIdx]);
  // R12-12 (fix 2): Tap-to-lock is now driven by MUI X Charts'
  // `<ChartsOnAxisClickHandler>` component rendered inside the chart
  // container (see below). It fires with the clicked axis dataIndex on
  // BOTH desktop and touch — no DOM-click-vs-pointerup race, no ref
  // mirroring, no reliance on `useAxisTooltip` state surviving
  // pointerup. The earlier attempts (outer Box onClick + `hoverIdxRef`
  // mirror, then "last non-null hover" ref) both failed because by
  // synthesized-click time the tooltip state had already been cleared
  // AND the DOM click event never reached the outer Box on some
  // configurations. Moving the handler inside the chart sidesteps
  // both.

  const sliced = useMemo<Sliced>(
    () => sliceByRange(
      account.curveDates,
      account.twrCurve,
      account.spyCurve,
      account.spyTotalCurve,
      account.equityCurve,
      range,
      customRange
    ),
    [account, range, customRange]
  );

  // Parse a YYYY-MM-DD string as a LOCAL-midnight Date so
  // `toLocaleDateString` shows the same calendar day regardless of
  // the user's timezone. `new Date("2026-04-13")` is interpreted by
  // JavaScript as UTC midnight; in any timezone west of UTC that
  // shifts BACK to "Apr 12" when formatted locally — which made
  // the crosshair label render Mondays as the previous Sunday.
  // Sergey 2026-04-24 flagged this as "weekends still here".
  const isoToLocalDate = (s: string): Date => {
    const [y, m, d] = s.split("-").map(Number);
    if (!y || !m || !d) return new Date(s);
    return new Date(y, m - 1, d);
  };
  const dateObjs = useMemo(() => sliced.dates.map(isoToLocalDate), [sliced.dates]);
  // Keep the forward-reference-safe length ref in sync for the
  // mobile auto-lock pointerup effect declared above.
  useEffect(() => {
    dateObjsLenRef.current = dateObjs.length;
  }, [dateObjs.length]);
  // Cashflows that fall inside the active window.
  //
  // Per Apr-2026 R12 feedback ("there is no synthetic opening balances"):
  // the mock data now seeds every account with a real day-0 deposit whose
  // amount equals the starting equity, so the ALL-range window begins on
  // the day the first deposit landed. No synthetic markers are injected
  // here — the first real cashflow IS the opening.
  //
  // The cashflow-layering curves downstream (Portfolio $, SPY $, SPY+D $,
  // Target $, ND) therefore skip xIdx === 0 when summing, because
  // `sliced.usd[0]` already reflects that opening deposit. Including it
  // again would double-count. The day-0 dot still renders in the scatter.
  const cashflowsInWindow: (Cashflow & { at: Date; xIdx: number })[] = useMemo(() => {
    if (dateObjs.length === 0) return [];
    const startAt = dateObjs[0]!;
    const endAt = dateObjs[dateObjs.length - 1]!;
    // Snap helper — nearest date index in the window.
    const snap = (t: number): number => {
      let best = 0;
      let bestDist = Infinity;
      for (let i = 0; i < dateObjs.length; i++) {
        const d = Math.abs(dateObjs[i]!.getTime() - t);
        if (d < bestDist) { bestDist = d; best = i; }
      }
      return best;
    };
    return (account.cashflows ?? [])
      .map((cf) => ({ ...cf, at: isoToLocalDate(cf.date) }))
      // Inclusive filter — ISO midnight timestamps should match exactly
      // at boundaries, but we use >= / <= defensively so cashflows
      // whose timezone rounding lands them right on the edge still show.
      .filter((cf) => cf.at.getTime() >= startAt.getTime() && cf.at.getTime() <= endAt.getTime())
      .map((cf) => ({ ...cf, xIdx: snap(cf.at.getTime()) }));
  }, [account.cashflows, dateObjs]);

  // Full-history dates + cashflows — used by the $-mode scenario curves
  // below so they can be computed from INCEPTION and then sliced to the
  // active window. This is what makes each line "start at its actual $
  // value" when the window doesn't include day 0 (per Apr-2026 R12-8
  // feedback: "When rebase is OFF and not ALL range selected that does
  // not include start day=0, all lines must start at their actual $
  // value"). Without it, every scenario's growth factor at window-left
  // collapses to 1 and all lines stack on top of `sliced.usd[0]`.
  const fullDateObjs = useMemo(
    () => account.curveDates.map(isoToLocalDate),
    [account.curveDates]
  );
  const allCashflowsWithFullIdx: (Cashflow & { at: Date; fullIdx: number })[] =
    useMemo(() => {
      if (fullDateObjs.length === 0) return [];
      const firstAt = fullDateObjs[0]!.getTime();
      const lastAt = fullDateObjs[fullDateObjs.length - 1]!.getTime();
      const snap = (t: number): number => {
        let best = 0;
        let bestDist = Infinity;
        for (let i = 0; i < fullDateObjs.length; i++) {
          const d = Math.abs(fullDateObjs[i]!.getTime() - t);
          if (d < bestDist) { bestDist = d; best = i; }
        }
        return best;
      };
      return (account.cashflows ?? [])
        .map((cf) => ({ ...cf, at: isoToLocalDate(cf.date) }))
        // Filter out cashflows whose date falls outside the curveDates
        // window — relevant for the aggregate account where the curves
        // are trimmed to the shortest common history but the cashflow
        // list is unioned across accounts. Without this, an older
        // cashflow would snap to fullIdx=0 and bake itself into the
        // aggregate's "opening" — corrupting every downstream $ curve.
        .filter(
          (cf) => cf.at.getTime() >= firstAt && cf.at.getTime() <= lastAt
        )
        .map((cf) => ({ ...cf, fullIdx: snap(cf.at.getTime()) }));
    }, [account.cashflows, fullDateObjs]);

  const isUsd = mode === "usd";
  const targetPct = targetPctOverride ?? account.targetApyPct ?? 20;

  // Opens the Target APY editor dialog. The actual number-picking UI
  // is a proper MUI Dialog (`TargetApyDialog` below) — no more
  // browser-native `window.prompt` eyesore.
  const editTargetPct = useCallback(() => {
    setTargetDialogOpen(true);
  }, []);

  // Target curves — compounding in both modes. In % mode it's the pure
  // compound-return curve (cashflow-independent, because TWR by
  // construction neutralises cashflows). In $ mode it compounds the
  // starting value AND adds every deposit/withdrawal growing from its
  // own date.
  const usdTargetCurve = useMemo(() => {
    if (!isUsd) return [] as number[];
    // Compound from INCEPTION so a sub-range starts at the Target
    // scenario's actual $ value at window-left (R12-8). `buildUsdTargetCurve`
    // anchors on the first element of its dates argument, so we pass
    // the FULL date array + inception equity + ALL cashflows (skipping
    // the day-0 opening deposit — already baked into the startValue),
    // then slice to the active window.
    const inceptionEquity = account.equityCurve[0] ?? 0;
    const fullTarget = buildUsdTargetCurve(
      fullDateObjs,
      inceptionEquity,
      targetPct,
      allCashflowsWithFullIdx
        .filter((cf) => cf.fullIdx > 0)
        .map((cf) => ({ at: cf.at, amount: cf.amount }))
    );
    const base = fullTarget.slice(
      sliced.startIdx,
      sliced.startIdx + sliced.usd.length
    );
    if (!effectiveRebased) return base;
    const openVal = base[0] ?? 0;
    return base.map((v) => +(v - openVal).toFixed(2));
  }, [
    isUsd,
    fullDateObjs,
    account.equityCurve,
    targetPct,
    allCashflowsWithFullIdx,
    sliced.startIdx,
    sliced.usd.length,
    effectiveRebased
  ]);

  // % mode Target curve.
  //
  // Two anchor modes:
  //
  //  • Not rebased → compound growth anchored at INCEPTION: a given
  //    calendar date reads the same Target % regardless of which range
  //    chip is active, matching the inception-anchored TWR / SPY / SPY+D
  //    lines.
  //
  //  • Rebased     → industry-standard pro-rata over the window:
  //
  //      - Named ranges use NOMINAL year fractions (broker-app convention,
  //        gives the clean round numbers users expect):
  //
  //          W    = 1 / 52      (1.67 % / yr × 1wk  ≈ 0.385 %)
  //          1M   = 1 / 12      (20  % / yr × 1mo  ≈ 1.667 %)
  //          3M   = 3 / 12      (20  % / yr × 3mo  = 5 %)
  //          6M   = 6 / 12      (20  % / yr × 6mo  = 10 %)
  //          1Y   = 12 / 12     (20  % / yr × 12mo = 20 %)
  //          YTD  = elapsed year fraction from Jan 1
  //
  //        Compound-daily math over the literal calendar-day window
  //        gives 9.54 % for 6M at a 20 % target — technically correct
  //        but nobody displays it that way. Broker UIs pro-rate linearly
  //        at nominal fractions so "3 months at 20 %/yr = +5 %" reads
  //        cleanly.
  //
  //      - CUSTOM falls back to actual calendar-days / 365 since the
  //        user's arbitrary window has no nominal label.
  //
  //      - ALL is treated as compound-since-inception (same as the
  //        non-rebased branch); on ALL range, window-open IS inception
  //        so "rebased vs not" is a no-op.
  //
  //    The curve itself grows linearly from 0 at window-open to
  //    `targetPct × yearFrac` at window-end — simple-interest display,
  //    not compound-daily. Hover on any intermediate day reads the
  //    pro-rata value at that day's fractional position in the window.
  const pctTargetCurve = useMemo(() => {
    if (isUsd || fullDateObjs.length === 0 || sliced.usd.length === 0) {
      return [] as number[];
    }
    const rate = targetPct / 100;
    const windowDates = fullDateObjs.slice(
      sliced.startIdx,
      sliced.startIdx + sliced.usd.length
    );
    const N = windowDates.length;

    if (effectiveRebased && range !== "ALL") {
      // Nominal year fraction for the selected range. Named ranges use
      // industry-standard fixed fractions; CUSTOM + fallback use
      // calendar-days / 365.
      let yearFrac = 0;
      let nominalDays = 0;
      if (range === "W") { yearFrac = 1 / 52; nominalDays = 7; }
      else if (range === "1M") { yearFrac = 1 / 12; nominalDays = 30; }
      else if (range === "3M") { yearFrac = 3 / 12; nominalDays = 90; }
      else if (range === "6M") { yearFrac = 6 / 12; nominalDays = 180; }
      else if (range === "1Y") { yearFrac = 1; nominalDays = 365; }
      else if (range === "YTD") {
        const now = new Date();
        const jan1 = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
        yearFrac = (now.getTime() - jan1.getTime()) / (365 * 86400e3);
        nominalDays = (now.getTime() - jan1.getTime()) / 86400e3;
      } else if (range === "CUSTOM" && N >= 2) {
        const ms =
          windowDates[N - 1]!.getTime() - windowDates[0]!.getTime();
        yearFrac = ms / (365 * 86400e3);
        nominalDays = ms / 86400e3;
      }

      // Truncated-history guard (Sergey 2026-08-03). The nominal
      // fractions above assume the window actually spans the range the
      // user picked. When the account is younger than that, it doesn't:
      // Stormwatch Pulse *c222 has history from Apr 14 2026 only, so
      // its "1Y" window is really 111 days — yet the Target line still
      // climbed to the full +20 %, implying a year of compounding that
      // never happened, and towering over a portfolio that had ~4
      // months to earn it.
      //
      // So: when the real span is MATERIALLY shorter than nominal,
      // pro-rate against the real span instead. The 10 % tolerance
      // keeps the clean broker-style numbers for full windows — a
      // "3M" window of trading days spans 89-90 calendar days, and we
      // don't want that rounding to turn a tidy +5 % into +4.88 %.
      const actualDays =
        N >= 2
          ? (windowDates[N - 1]!.getTime() - windowDates[0]!.getTime()) / 86400e3
          : 0;
      if (nominalDays > 0 && actualDays < nominalDays * 0.9) {
        yearFrac = actualDays / 365;
      }

      const endPct = targetPct * yearFrac;
      return windowDates.map((_, i) => {
        const frac = N > 1 ? i / (N - 1) : 0;
        return +(endPct * frac).toFixed(4);
      });
    }

    // Non-rebased (or ALL range) → inception-anchored compound.
    const inceptionT = fullDateObjs[0]!.getTime();
    return windowDates.map((d) => {
      const yrs = (d.getTime() - inceptionT) / (365 * 86400e3);
      return +((Math.pow(1 + rate, yrs) - 1) * 100).toFixed(4);
    });
  }, [
    isUsd,
    fullDateObjs,
    sliced.startIdx,
    sliced.usd.length,
    targetPct,
    effectiveRebased,
    range
  ]);

  const fmtPctSigned = (v: number, digits = 2) => `${v >= 0 ? "+" : ""}${v.toFixed(digits)}%`;
  const fmtUsd = (v: number) => new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", maximumFractionDigits: 0
  }).format(v);

  // Display curves — when `rebased` is on AND we're in % mode, shift
  // Portfolio & SPY so both start at 0% at the window's left edge.
  // These arrays are what the chart and the chips actually read from,
  // so everything (line, hover dots, chip values, cashflow-dot y) stays
  // in sync automatically. Target isn't shifted: it's a forward-looking
  // compounding curve that starts at 0% by construction.
  const twrDisplay = useMemo(() => {
    if (isUsd || !effectiveRebased || sliced.twr.length === 0) return sliced.twr;
    return rebaseCumulativePct(sliced.twr);
  }, [isUsd, effectiveRebased, sliced.twr]);

  const spyDisplay = useMemo(() => {
    if (isUsd || !effectiveRebased || sliced.spy.length === 0) return sliced.spy;
    return rebaseCumulativePct(sliced.spy);
  }, [isUsd, effectiveRebased, sliced.spy]);

  // Portfolio's ACTUAL $ value at every index of the active window,
  // fully cashflow-layered from inception. This is the "source of
  // truth" dollar curve used by:
  //   1. `usdDisplay` ($-mode Portfolio line) — optionally rebased.
  //   2. The hero hover effect (big $ number + delta chip) — in BOTH
  //      $ and % modes, because the hero always renders absolute
  //      dollars regardless of chart mode.
  //
  // Computed from INCEPTION so a sub-range's left edge reflects the
  // portfolio's real $ at that moment in history — any deposits or
  // withdrawals that landed before the window are already compounded
  // in. Pre-R12-8 we layered only in-window cashflows on top of
  // `sliced.usd[0]`, which collapsed every $-mode line onto the same
  // opening value for sub-ranges.
  //
  // Model: treat `account.equityCurve[i]` as a return index anchored
  // at account inception, then add every cashflow AFTER day 0, each
  // growing forward at the same market rate from the day it lands.
  // The day-0 opening deposit is skipped because `equityCurve[0]`
  // already equals that opening amount by construction.
  //
  //   fullFromInception[i] = equityCurve[i]
  //                        + Σ cf.amount * equityCurve[i] / equityCurve[cf.fullIdx]
  //     for every cf with 0 < fullIdx <= i
  //
  //   fullPortfolioUsd = fullFromInception.slice(startIdx, startIdx + window)
  //
  // Runs unconditionally (not mode-gated) — in % mode the line on the
  // chart shows TWR percentages, but the hero still needs the real
  // dollar value at the crosshair. Previously that read from
  // `sliced.usd` in % mode (raw equity walk, no cashflow layering),
  // which when combined with the "subtract cashflowContribution"
  // logic in the hero effect produced catastrophically wrong
  // percentages (Apr-2026 R12-8 follow-up: "Portfolio % shows
  // -10.6k%"). The fix is to make the hero's dollar source consistent
  // across both modes, which this memo provides.
  const fullPortfolioUsd = useMemo(() => {
    if (account.equityCurve.length === 0 || sliced.usd.length === 0) {
      return [] as number[];
    }
    const equity = account.equityCurve;
    // "total-equity" basis (real accounts): equityCurve already
    // reflects every deposit/withdrawal — states include cash+stocks,
    // pre-state backfill is cumulative net deposits. Re-applying the
    // layering formula here would double-count the cashflows and
    // inflate the $ curve + every hero chip that reads from it.
    // Return equityCurve directly, sliced to window.
    if (account.portfolioBasis === "total-equity") {
      return equity.slice(sliced.startIdx, sliced.startIdx + sliced.usd.length);
    }
    const real = allCashflowsWithFullIdx.filter((cf) => cf.fullIdx > 0);
    const full = real.length === 0
      ? equity.slice()
      : equity.map((baseAtI, i) => {
          let v = baseAtI;
          for (const cf of real) {
            if (cf.fullIdx > i) continue;
            const anchor = equity[cf.fullIdx];
            if (anchor == null || anchor === 0) continue;
            v += cf.amount * (baseAtI / anchor);
          }
          return +v.toFixed(2);
        });
    return full.slice(sliced.startIdx, sliced.startIdx + sliced.usd.length);
  }, [
    account.equityCurve,
    account.portfolioBasis,
    allCashflowsWithFullIdx,
    sliced.startIdx,
    sliced.usd.length
  ]);

  // $-mode Portfolio display curve — the raw equityCurve is a market-
  // PnL-only walk (see `walk()` in mock-data) and does NOT contain the
  // step-ups / step-downs that deposits and withdrawals should cause.
  // Without cashflow layering the Portfolio line in $ mode looks flat
  // through a $2,000 deposit event while the ND line jumps — an
  // internally-inconsistent picture that confused users in Apr-2026
  // feedback ("switched to [$] the Portfolio line stay the same and
  // not reflecting deposits and withdrawals"). The layered data comes
  // from `fullPortfolioUsd` above; here we just apply the optional
  // $-mode rebase offset (subtract window-opening so the line reads
  // as PnL in dollars — Apr-2026 R11: "Where you got rebase (start
  // all from 0) on $ graph?").
  //
  // % mode is untouched — TWR is cashflow-agnostic by construction
  // and the chart plots `sliced.twr` directly (via `twrDisplay`).
  const usdDisplay = useMemo(() => {
    if (!isUsd || sliced.usd.length === 0) return sliced.usd;
    if (!effectiveRebased) return fullPortfolioUsd;
    const opening = fullPortfolioUsd[0] ?? 0;
    return fullPortfolioUsd.map((v) => +(v - opening).toFixed(2));
  }, [isUsd, sliced.usd, fullPortfolioUsd, effectiveRebased]);

  // Active index drives the chip values and the date label. CHART-SPEC
  // rule (R12-12 refined): `activeIdx = lockedIdx ?? hoverIdx ?? last`.
  // **Lock wins over hover** — once a date is pinned (desktop click, or
  // mobile release), moving the pointer does not shift the hero values
  // or the crosshair. The user must clear the lock (× / Escape /
  // range-mode-account change) before hover/tap can select another
  // date. This matches the desktop "click to pin, then inspect at
  // leisure" model and the mobile "touch-release snapshots what you
  // were looking at" model. When neither is set, fall back to the
  // window's last index (the "current" state).
  const lockedSafe =
    lockedIdx != null && lockedIdx >= 0 && lockedIdx < dateObjs.length
      ? lockedIdx
      : null;
  const hoverSafe =
    hoverIdx != null && hoverIdx >= 0 && hoverIdx < dateObjs.length
      ? hoverIdx
      : null;
  const activeIdx =
    lockedSafe ?? hoverSafe ?? Math.max(0, dateObjs.length - 1);

  // Chips read the *display* curves so that rebase toggling flows
  // through to the chip values as well as the lines. `usdDisplay`
  // (cashflow-adjusted) is what the user sees on the chart in $ mode,
  // so the Portfolio chip's dollar value must come from the same
  // array — otherwise clicking a dot on a deposit day showed the
  // pre-deposit equity in the chip while the line sat visibly higher.
  const portAt   = twrDisplay[activeIdx] ?? 0;
  const spyAt    = spyDisplay[activeIdx] ?? 0;
  const usdAt    = usdDisplay[activeIdx] ?? 0;
  const targetUsdAt = usdTargetCurve[activeIdx] ?? 0;
  const targetPctAt = pctTargetCurve[activeIdx] ?? 0;

  // Portfolio / Target chip values — reactive to the crosshair. SPY
  // and SPY+Div chip values are declared later (after their respective
  // curves are computed) because those curves live in the memo block
  // further down the file.
  const portValue   = isUsd ? fmtUsd(usdAt) : fmtPctSigned(portAt);
  const targetValue = isUsd ? fmtUsd(targetUsdAt) : `+${targetPctAt.toFixed(2)}%`;

  // Compact chip labels — keep all 4 chips on one line on phone widths.
  const portLabel = compact ? "P" : "Portfolio";
  const targetLabel = compact
    ? `T (${targetPct.toFixed(0)}%)`
    : `Target (${targetPct.toFixed(0)}%)`;
  const cashflowLabel = compact ? "Cash" : "Cashflow";

  // Cashflow at the currently-hovered date, if any. We look *only* at
  // `hoverIdx` (not `activeIdx`), so the cashflow chip's value only
  // appears while the crosshair is directly over a deposit/withdrawal
  // dot — not as a permanent value on the chip like Portfolio / SPY /
  // Target. This keeps the chip acting as a legend at rest, and as a
  // live reader on hover.
  const cashflowAtHover = hoverIdx != null
    ? cashflowsInWindow.find((cf) => cf.xIdx === hoverIdx)
    : undefined;
  const cashflowValue = cashflowAtHover
    ? `${cashflowAtHover.amount >= 0 ? "+" : "−"}${fmtUsd(Math.abs(cashflowAtHover.amount))}`
    : undefined;
  const cashflowPositive = cashflowAtHover ? cashflowAtHover.amount >= 0 : true;

  // "SPY + Dividends" (total return) curve — driven by the SAME
  // `spyTotalCurve` field the WHB weekly report and the ADJ-style
  // Portfolio vs SPY chart read from. That series already bakes in
  // every received dividend (reinvested into SPY shares at the close of
  // the dividend date), so here we just rebase + slice and call it
  // done. This replaces an earlier continuous-compound approximation
  // that ignored discrete dividend events — per Apr-2026 feedback the
  // calculation must match the received-dividends model used
  // everywhere else in the app.
  //
  // Rebase: when the user has pressed "Rebase" in % mode, shift the
  // total-return series so it also starts at 0 at the window's left
  // edge — otherwise SPY+Div sits several percent away from SPY on a
  // rebased range slice, which looks broken.
  const spyDivPctCurve = useMemo(() => {
    if (sliced.spyTotal.length === 0) return [] as number[];
    if (!effectiveRebased) return sliced.spyTotal;
    return rebaseCumulativePct(sliced.spyTotal);
  }, [sliced.spyTotal, effectiveRebased]);

  // SPY $-mode curve — "what would Portfolio be worth if it had been
  // invested in SPY with the same cashflows". Apr-2026 R11 feedback:
  // "Why SPY and SPY+D values not included Deposits/withdrawals? This
  // is comparison as if Portfolio invested in to SPY."
  //
  // Computed from INCEPTION (R12-8) — growth factor is SPY's price-only
  // relative walk anchored at day 0 of the account. Inception equity
  // plus every cashflow is grown forward at the SPY rate from the day
  // it lands; the full curve is then sliced to the active window so a
  // sub-range starts at SPY's actual $ value at window-left instead of
  // collapsing onto the portfolio's equity.
  //
  //   spyRelFull[i] = 1 + account.spyCurve[i] / 100
  //   spyUsdFull[i] = inceptionEquity * spyRelFull[i]
  //                 + Σ cf.amount * (spyRelFull[i] / spyRelFull[cf.fullIdx])
  //     for every cf with 0 < fullIdx <= i
  //   spyUsdCurve   = spyUsdFull.slice(startIdx, startIdx + window.length)
  const spyUsdCurve = useMemo(() => {
    if (
      account.equityCurve.length === 0 ||
      account.spyCurve.length === 0 ||
      sliced.usd.length === 0
    ) return [] as number[];
    const inceptionEquity = account.equityCurve[0] ?? 0;
    const spyRelFull = account.spyCurve.map((pct) => 1 + pct / 100);
    // Skip fullIdx=0 — the inception deposit is already represented by
    // `inceptionEquity * spyRelFull[i]`; layering it on top would
    // double the curve.
    const real = allCashflowsWithFullIdx.filter((cf) => cf.fullIdx > 0);
    const full = spyRelFull.map((relAtI, i) => {
      let v = inceptionEquity * relAtI;
      for (const cf of real) {
        if (cf.fullIdx > i) continue;
        const anchor = spyRelFull[cf.fullIdx];
        if (anchor == null || anchor === 0) continue;
        v += cf.amount * (relAtI / anchor);
      }
      return +v.toFixed(2);
    });
    const base = full.slice(sliced.startIdx, sliced.startIdx + sliced.usd.length);
    if (!effectiveRebased) return base;
    const openVal = base[0] ?? 0;
    return base.map((v) => +(v - openVal).toFixed(2));
  }, [
    account.equityCurve,
    account.spyCurve,
    allCashflowsWithFullIdx,
    sliced.startIdx,
    sliced.usd.length,
    effectiveRebased
  ]);

  // SPY+Dividends $-mode curve — same "invested in SPY with same
  // cashflows" formula as `spyUsdCurve` but using the total-return
  // series (dividends reinvested) as the growth factor. Computed from
  // INCEPTION (R12-8) so a sub-range starts at SPY+D's actual $ value
  // at window-left. `account.spyTotalCurve` is already anchored at 0%
  // at inception, so `totalRelFull[0] = 1` — no need to re-rebase.
  const spyDivUsdCurve = useMemo(() => {
    if (
      account.equityCurve.length === 0 ||
      account.spyTotalCurve.length === 0 ||
      sliced.usd.length === 0
    ) return [] as number[];
    const inceptionEquity = account.equityCurve[0] ?? 0;
    const totalRelFull = account.spyTotalCurve.map((pct) => 1 + pct / 100);
    // Skip fullIdx=0 — already accounted for by `inceptionEquity * totalRelFull[i]`.
    const real = allCashflowsWithFullIdx.filter((cf) => cf.fullIdx > 0);
    const full = totalRelFull.map((relAtI, i) => {
      let v = inceptionEquity * relAtI;
      for (const cf of real) {
        if (cf.fullIdx > i) continue;
        const anchor = totalRelFull[cf.fullIdx];
        if (anchor == null || anchor === 0) continue;
        v += cf.amount * (relAtI / anchor);
      }
      return +v.toFixed(2);
    });
    const base = full.slice(sliced.startIdx, sliced.startIdx + sliced.usd.length);
    if (!effectiveRebased) return base;
    const openVal = base[0] ?? 0;
    return base.map((v) => +(v - openVal).toFixed(2));
  }, [
    account.equityCurve,
    account.spyTotalCurve,
    allCashflowsWithFullIdx,
    sliced.startIdx,
    sliced.usd.length,
    effectiveRebased
  ]);

  // Legacy alias used throughout the chart for the "SPY+X" line data
  // and chip value. Points at whichever mode-appropriate curve is
  // active so the rendering code below stays branch-free.
  const spyDivCurve = isUsd ? spyDivUsdCurve : spyDivPctCurve;
  const spyDivAt = spyDivCurve[activeIdx] ?? 0;

  // SPY / SPY+Div chip values — declared here (after the curves they
  // depend on) rather than with the other chip values further up, so
  // they can reference spyUsdCurve / spyDivCurve / spyDivAt without
  // tripping the TDZ. `spyValue`/`spyDivValue` are the formatted
  // strings the chips render; `spyUsdAt` drives the positive/negative
  // colouring in $ mode (compared against the window's opening USD
  // value so "flat" reads as positive only when we're actually up).
  const spyUsdAt    = spyUsdCurve[activeIdx] ?? 0;
  const spyValue    = isUsd ? fmtUsd(spyUsdAt) : fmtPctSigned(spyAt);
  const spyDivValue = isUsd ? fmtUsd(spyDivAt) : fmtPctSigned(spyDivAt);

  // Net Deposits — stepped horizontal line that changes only at
  // cashflow events. Per Sergey's Round-10 spec: "combination of
  // horizontal lines, as value changes only on amount of added
  // deposit/withdrawal". $-mode only.
  //
  //   ND$[i] = Σ cf.amount for every cashflow in the ACCOUNT (from
  //            inception) with fullIdx <= i, then sliced to the window
  //
  // Visual: starts at the actual cumulative net-deposit balance at
  // window-left (R12-8 — so a sub-range that opens after several
  // prior deposits shows the carried-forward total, not $0), jumps at
  // each later cashflow, stays flat otherwise. Renders with stepAfter.
  //
  // IMPORTANT — do NOT anchor on `sliced.usd[0]`. walk() in mock-data
  // mutates `v` before its first push, so equityCurve[0] is
  // `start × (1 + drift ± vol)` rather than `start`. Anchoring ND on
  // that value leaked random-walk drift into a cashflow-only ledger,
  // producing a chip total that disagreed with the Net Deposits metric
  // card by the per-day drift amount. ND is pure money-in/money-out,
  // so it starts at $0 at inception and accumulates every cashflow —
  // the end-of-account value then matches `Σ account.cashflows` by
  // construction.
  // Two parallel ND series:
  //
  //   • netDepositsAbsoluteCurve — running Σ cashflows, never rebased.
  //                                 Drives the LEGEND CHIP value.
  //   • netDepositsCurve         — same series, rebased to $0 at the
  //                                 window's first point when in
  //                                 rebase mode. Drives the CHART LINE
  //                                 so its shape lines up with the
  //                                 rebased Portfolio / Target / SPY
  //                                 lines.
  //
  // Sergey 2026-04-30 caught that the chip was rebasing too — on the
  // ALL range of an account with a Sep 2 +$10,030 deposit on day 0,
  // rebasing subtracted $10,030 from every value, so the chip read
  // $8,950 instead of the $18,980 lifetime ND. ND is a *state*
  // (cumulative money in/out), not a return — rebasing it for the
  // chip is semantically wrong. The line still rebases because that's
  // a visual choice; the chip number is a question ("how much have I
  // deposited?") with one right answer.
  const netDepositsAbsoluteCurve = useMemo(() => {
    if (!isUsd || dateObjs.length === 0) return [] as number[];
    const byFullIdx = new Map<number, number>();
    for (const cf of allCashflowsWithFullIdx) {
      byFullIdx.set(cf.fullIdx, (byFullIdx.get(cf.fullIdx) ?? 0) + cf.amount);
    }
    const full: number[] = [];
    let running = 0;
    for (let i = 0; i < fullDateObjs.length; i++) {
      running += byFullIdx.get(i) ?? 0;
      full.push(+running.toFixed(2));
    }
    return full.slice(sliced.startIdx, sliced.startIdx + sliced.usd.length);
  }, [
    isUsd,
    dateObjs.length,
    fullDateObjs.length,
    allCashflowsWithFullIdx,
    sliced.startIdx,
    sliced.usd.length
  ]);

  const netDepositsCurve = useMemo(() => {
    if (!effectiveRebased) return netDepositsAbsoluteCurve;
    // Rebased: subtract the opening value so the LINE starts at $0 and
    // its shape sits next to the other rebased lines on the same y-axis.
    const openVal = netDepositsAbsoluteCurve[0] ?? 0;
    return netDepositsAbsoluteCurve.map((v) => +(v - openVal).toFixed(2));
  }, [effectiveRebased, netDepositsAbsoluteCurve]);

  // Chip reads from the ABSOLUTE series — always lifetime cumulative.
  const netDepositsAt = netDepositsAbsoluteCurve[activeIdx] ?? 0;

  // Chip label + value. ND is $-mode only so value is always dollars;
  // sign colouring compares against the absolute curve's own opening
  // value so a flat line reads as neutral.
  const netDepositLabel = compact ? "ND" : "Net Deposits";
  const netDepositValue = fmtUsd(netDepositsAt);
  const netDepositPositive =
    netDepositsAt >= (netDepositsAbsoluteCurve[0] ?? 0);

  // ---- SMA — trailing simple moving average over the displayed line ----
  //
  // Sergey 2026-04-30. Window comes from the user's smaWindowMode:
  //   • "auto"  → defaultSmaWindowForRange(range, ...)
  //   • "fixed" → user's smaWindowDays
  //
  // Computed on the SAME displayed curve the chart renders (usdDisplay
  // in $ mode, twrDisplay in %), so the smoothing tracks what the user
  // is looking at. Trailing convention; first `window-1` points are
  // null so MUI X line series leaves a gap at the start of the line —
  // the SMA naturally appears once enough data has accumulated.
  const smaRangeKey = (range as SmaRangeKey) ?? "ALL";
  // Rough lifespan of the account in calendar days — feeds the
  // ALL-range bucket's choice between 50d and 100d auto SMA.
  const accountLifespanDays = useMemo(() => {
    if (account.curveDates.length < 2) return 0;
    const first = account.curveDates[0];
    const last = account.curveDates[account.curveDates.length - 1];
    if (!first || !last) return 0;
    return Math.max(
      0,
      Math.round(
        (new Date(last).getTime() - new Date(first).getTime()) /
          (24 * 60 * 60 * 1000)
      )
    );
  }, [account.curveDates]);
  const customWindowDays = useMemo(() => {
    if (range !== "CUSTOM" || dateObjs.length < 2) return 0;
    const first = dateObjs[0]!.getTime();
    const last = dateObjs[dateObjs.length - 1]!.getTime();
    return Math.max(0, Math.round((last - first) / (24 * 60 * 60 * 1000)));
  }, [range, dateObjs]);
  const smaWindow = useMemo(
    () =>
      resolveSmaWindow(
        smaWindowMode,
        smaWindowDays,
        smaRangeKey,
        customWindowDays,
        accountLifespanDays
      ),
    [smaWindowMode, smaWindowDays, smaRangeKey, customWindowDays, accountLifespanDays]
  );
  const smaCurve = useMemo<Array<number | null>>(() => {
    if (smaWindow < 2 || !showSma) return [];
    const source = isUsd ? usdDisplay : twrDisplay;
    if (source.length === 0) return [];
    return computeSMA(source, smaWindow);
  }, [showSma, smaWindow, isUsd, usdDisplay, twrDisplay]);
  // Chip value — SMA at the active hover/lock index. Falls back to "—"
  // when the window's still filling at activeIdx (we render whatever
  // the curve has there; null becomes em-dash on the chip).
  const smaAt: number | null =
    smaCurve.length > 0 ? smaCurve[activeIdx] ?? null : null;

  // Scatter data for cashflow dots — x = date, y = portfolio value at
  // that index (so the dot sits on the line). Reads `twrDisplay` /
  // `usdDisplay` so dots always track whichever line the user is
  // seeing (rebased TWR in % mode, cashflow-adjusted portfolio in $
  // mode). Prior rev read `sliced.usd` directly, which left deposit
  // dots floating off the line after the $-mode cashflow fix.
  //
  // Every cashflow in the window is plotted — including the day-0
  // opening deposit, which is a real row in the mock data now (no
  // more synthetic markers). The ND metric card counts the same rows,
  // so chart-dot count and "N deposits · M withdrawals" stay in sync.
  const cashflowScatter = cashflowsInWindow.map((cf) => ({
    // MUI X Charts v7 matches scatter `x` to xAxis entries BY VALUE
    // on a categorical (point-scale) axis — not by index. The xAxis
    // here is `scaleType: "point"` with `data: dateObjs` (Date[]),
    // so the scatter point must reference the same Date OBJECT that
    // sits in `dateObjs[xIdx]`; passing the numeric `xIdx` matched
    // nothing and the series rendered with zero markers (Sergey
    // 2026-04-30 — DOM probe showed `series=5 (3 line + 2 scatter)`
    // but `circles=0`). Reference equality is fine here because
    // `dateObjs` is the same array MUI X is iterating over for
    // axis tick alignment.
    x: dateObjs[cf.xIdx]!,
    y: isUsd ? usdDisplay[cf.xIdx]! : twrDisplay[cf.xIdx]!,
    color: cf.type === "deposit" ? COLORS.deposit : COLORS.withdrawal,
    cf
  }));

  // X-axis domain — padded slightly beyond the data range so the first
  // and last points don't sit flush against the plot-area boundary. Without
  // padding, the cashflow scatter dot at day 0 (and at the latest day) is
  // half-clipped by the plot area's clip-path, and the line stroke is
  // shaved by half a pixel at each end too. Apr-2026 R12 feedback: "add
  // enough space on right and left end so full shape of cashflow dots is
  // not cut off". 1.5% of the visible span on each side is enough room
  // for a ~6 px marker + its stroke at typical chart widths, while being
  // small enough that the data still fills the plot area visually.
  // xPadMs/xMin/xMax used to drive the time-scale's left/right
  // gutter and the tap-to-lock click→time mapping. Removed
  // 2026-04-24 with the scaleType: "time" → "point" switch — point
  // scale auto-fits to its data array and the tap handler now maps
  // click x-percentage directly to a categorical index.

  // Tight y-axis bounds (V2 only). Auto-padding from MUI X Charts leaves
  // dead air above/below the plotted lines, which wastes vertical space
  // on mobile. We compute min/max across every *visible* series (so
  // toggling SPY/Target/ND reflows the y-axis) plus a small pad so the
  // extrema don't sit flush against the top/bottom edge. V1 keeps the
  // auto-padded axis — changing V1 here would regress the V1 screenshots.
  const yDomain = useMemo(() => {
    if (!v2) return { min: undefined as number | undefined, max: undefined as number | undefined };
    const values: number[] = [];
    if (isUsd) {
      // Use the DISPLAY curve (with cashflows layered in) so the y-axis
      // fits the line the user actually sees — otherwise a big deposit
      // late in the window pushes the line above the top edge.
      values.push(...usdDisplay);
      if (showTarget && usdTargetCurve.length) values.push(...usdTargetCurve);
      if (showNetDeposits && netDepositsCurve.length) values.push(...netDepositsCurve);
      if (showSpy && spyUsdCurve.length) values.push(...spyUsdCurve);
      if (showSpyDiv && spyDivCurve.length) values.push(...spyDivCurve);
    } else {
      values.push(...twrDisplay);
      if (showSpy && spyDisplay.length) values.push(...spyDisplay);
      if (showTarget && pctTargetCurve.length) values.push(...pctTargetCurve);
      if (showSpyDiv && spyDivCurve.length) values.push(...spyDivCurve);
    }
    if (values.length === 0) return { min: undefined, max: undefined };
    const lo = Math.min(...values);
    const hi = Math.max(...values);
    const span = Math.max(hi - lo, Math.abs(hi) * 0.001, 1e-6);
    const pad = span * 0.04;
    return { min: lo - pad, max: hi + pad };
  }, [
    v2, isUsd, usdDisplay, twrDisplay, spyDisplay, spyUsdCurve, pctTargetCurve,
    usdTargetCurve, netDepositsCurve, spyDivCurve,
    showSpy, showTarget, showNetDeposits, showSpyDiv
  ]);

  // Emit hover state to the parent page so the hero "Total Portfolio"
  // value + delta can react to the crosshair. We always send *either* a
  // concrete state or `null` (cursor left chart) — the consumer decides
  // whether to render the live value or fall back to the account
  // snapshot. Changes are rate-limited naturally by React's effect
  // batching, so this won't thrash on every mouse event.
  useEffect(() => {
    if (!onHoverState) return;
    // Window must be non-empty and have a usable dollar curve. If not,
    // emit null so the consumer can fall back to its own snapshot.
    if (dateObjs.length === 0 || fullPortfolioUsd.length === 0) {
      onHoverState(null);
      return;
    }
    // CHART-SPEC rule (R12-12 refined): `activeIdx = lockedIdx ??
    // hoverIdx ?? last`. Lock wins over hover so the hero stays
    // pinned while the user hovers/mouses around. Hover only drives
    // the hero when nothing is locked. Window-end is the fallback.
    // `isHover` in the emitted payload means "a specific date is
    // being shown" — true for both hover AND lock — so the consumer
    // (hero) renders the picked date instead of the range tagline.
    const lastIdx = dateObjs.length - 1;
    const hovering =
      hoverIdx != null && hoverIdx >= 0 && hoverIdx < dateObjs.length;
    const locked =
      lockedIdx != null && lockedIdx >= 0 && lockedIdx < dateObjs.length;
    const activeIdx = locked ? lockedIdx! : hovering ? hoverIdx! : lastIdx;
    const specificDateActive = hovering || locked;

    // Hero reads from `fullPortfolioUsd` — the always-layered,
    // mode-independent dollar curve — so the big $ number + delta chip
    // are coherent in BOTH $ and % modes. Previously the hero pulled
    // from `usdDisplay`, which in % mode returns the raw `sliced.usd`
    // (no cashflow layering). That combined with the "subtract
    // cashflowContribution" logic below produced wildly wrong
    // percentages (Apr-2026 R12-8 follow-up: hero showed "-10.6k%"
    // while the chart lines were visually crossed near 0%).
    //
    // `curUsd` is the portfolio's absolute $ value at `activeIdx`
    // with all prior + in-window cashflows already compounded in.
    // `baseUsdAbs` is the same curve's value at the window's left edge
    // — the opening balance the delta chips read as "since".
    //
    // Cashflow subtraction: the delta chip shows *market returns*, not
    // "returns + money you deposited", so we subtract every post-
    // opening cashflow inside the window. xIdx=0 is the opening
    // deposit — already baked into `baseUsdAbs` — so we skip it.
    //
    // `pctChange` is emitted as a DECIMAL (e.g. 0.05 for +5%) to match
    // `account.allTimeChangePct` and the `DeltaBadge` / `formatPct`
    // contract (formatPct multiplies by 100 internally). Emitting a
    // percentage here produced a 100× inflation in the rendered chip.
    const baseUsdAbs = fullPortfolioUsd[0] ?? 0;
    const curUsd = fullPortfolioUsd[activeIdx] ?? 0;
    const cashflowContribution = cashflowsInWindow
      .filter((cf) => cf.xIdx > 0 && cf.xIdx <= activeIdx)
      .reduce((sum, cf) => sum + cf.amount, 0);
    const usdChange = curUsd - baseUsdAbs - cashflowContribution;
    // pctChange source depends on `portfolioBasis`:
    //
    //   • "market-only" (mock) — `baseUsdAbs` is the window-open market
    //     value (contract: equityCurve is a market walk + layered
    //     cashflows), so `usdChange / baseUsdAbs` is a sensible market
    //     return. This matches the pre-2026-04-22 formula.
    //
    //   • "total-equity" (real accounts) — `baseUsdAbs` is whatever the
    //     account's total value was at window-open (for ALL range that's
    //     the first cashflow amount, e.g. a $7,597 initial deposit). That's
    //     NOT working capital — dividing a whole-portfolio $ change by
    //     a tiny first-deposit figure inflates the percentage by 10×+.
    //     Instead read cashflow-adjusted return directly from
    //     `account.twrCurve` (built by `computeChainLinkedTWR` so it's
    //     already cashflow-neutral) and compute the window-scoped
    //     compound return: (1+twr_cur) / (1+twr_open) − 1.
    let pctChange: number;
    if (account.portfolioBasis === "total-equity") {
      // Money-weighted return over the VISIBLE window for real
      // accounts:
      //
      //   pct = (endEquity − startEquity − netFlows) / (startEquity + netFlows)
      //
      // i.e. the same numerator as the dollar change above, over the
      // capital that was actually at work during the window (the
      // opening balance plus anything paid in while it was open). The
      // two always agree because they share a numerator.
      //
      // BUG FIX (Sergey 2026-08-03): this used to divide by
      // `depositsInWindow` — the sum of deposits made *inside* the
      // window — ignoring the opening balance entirely. On the ALL
      // range that happens to be right (opening balance is zero, so
      // in-window deposits ARE the invested capital), which is why it
      // shipped. On every shorter range it's catastrophically wrong:
      // a $2.5k top-up inside the last year turned a ~+39% year into
      //
      //   (107,421 − 2,550) / 2,550 = +4,112%
      //
      // — the "+4006.54%" reported on the 1Y range. 3M / 1M were
      // inflated the same way. Adding `baseUsdAbs` restores the
      // opening balance to the denominator and degrades cleanly to the
      // old behaviour on ALL (where `baseUsdAbs` ≈ 0).
      const denom = baseUsdAbs + cashflowContribution;
      pctChange =
        denom > 0
          ? usdChange / denom
          : baseUsdAbs === 0
            ? 0
            : usdChange / baseUsdAbs;
    } else {
      pctChange = baseUsdAbs === 0 ? 0 : usdChange / baseUsdAbs;
    }
    const dateLabel = dateObjs[activeIdx]!.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric"
    });
    onHoverState({
      idx: activeIdx,
      usd: curUsd,
      pctChange,
      usdChange,
      dateLabel,
      isHover: specificDateActive
    });
  }, [
    onHoverState,
    hoverIdx,
    lockedIdx,
    dateObjs,
    fullPortfolioUsd,
    cashflowsInWindow,
    account.portfolioBasis,
    account.twrCurve,
    sliced.startIdx,
  ]);

  // Emit range-change info to the parent so the hero can render
  // Robinhood-style "Past week" / "All time" / "in range" after the
  // $ number when the user isn't hovering. Fires on mount + whenever
  // the range (or customRange — the label changes when the user picks
  // new dates even though `range` stays "CUSTOM") flips.
  useEffect(() => {
    if (!onRangeChange) return;
    const customLabel = range === "CUSTOM" && customRange
      ? formatCustomRangeLabel(customRange)
      : null;
    const startIso = sliced.dates[0] ?? "";
    const endIso = sliced.dates[sliced.dates.length - 1] ?? "";
    onRangeChange({
      key: range,
      label: rangeLabel(range),
      customLabel,
      startIso,
      endIso
    });
  }, [onRangeChange, range, customRange, sliced.dates]);

  // Scrollable chip-rail ref — used to center the active range chip on
  // tap (V2 only). Mirrors the AccountTabs tab-strip pattern but lands
  // the *clicked* chip at centre rather than the previous one at left.
  const rangeRailRef = useRef<HTMLDivElement>(null);

  // Scroll the chip that corresponds to `key` so it sits in the middle
  // of the rail's visible region. No-op when the rail isn't mounted
  // (V1 path, which doesn't use the scrollable rail).
  const centerRangeChip = useCallback((key: RangeKey | "CUSTOM") => {
    const rail = rangeRailRef.current;
    if (!rail) return;
    // Land *after* React has committed the chip's new filled variant
    // so the measured width reflects the active state (the filled chip
    // is very slightly wider due to border collapse).
    requestAnimationFrame(() => {
      const rail2 = rangeRailRef.current;
      if (!rail2) return;
      const sel = rail2.querySelector<HTMLElement>(`[data-range-key="${key}"]`);
      if (!sel) return;
      const railRect = rail2.getBoundingClientRect();
      const selRect = sel.getBoundingClientRect();
      // Delta to land the chip's center on the rail's center.
      const delta =
        (selRect.left + selRect.width / 2) -
        (railRect.left + railRect.width / 2);
      rail2.scrollBy({ left: delta, behavior: "smooth" });
    });
  }, []);

  // Center the active chip on mount + whenever `range` changes. Covers
  // first-load (the default "ALL" was partially visible because the
  // rail just anchored to the left-most "W" chip) and also belt-and-
  // braces any programmatic range change (e.g. Calendar-picked CUSTOM)
  // where the onClick-based centering wouldn't fire.
  useEffect(() => {
    if (!v2) return;
    centerRangeChip(range);
  }, [v2, range, centerRangeChip]);

  // V2 puts NO outer Card padding and borderless — the page own its
  // gutters and the chart reads as a bleed-to-edge hero element (matches
  // Robinhood/Webull). V1 keeps the Card wrapper for the boxed look.
  const cardSx = v2
    ? { p: 0, overflow: "hidden", bgcolor: "transparent", boxShadow: "none", border: 0 }
    : { p: compact ? 2 : 3, overflow: "hidden" };

  return (
    <Card sx={cardSx}>
      {/* V1 header: title · (i) info · $/% toggle · hide/show. The info
       *  icon sits *tight* against the title so it reads as
       *  "Portfolio Graph ⓘ" — that's what Sergey asked for in Round 3.
       *  When `hidden` is on, we strip everything except the title +
       *  the eye toggle so the collapsed card is a single slim row.
       *
       *  V2 skips this row entirely — the "Portfolio Graph" overline is
       *  gone per spec, and the $/% toggle + eye have moved down into
       *  the range chip row below the chart. When `hidden` is on in V2
       *  we still need an eye to bring the chart back, so we render a
       *  minimal one-icon-row instead. */}
      {!v2 && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ mb: hidden ? 0 : 1.5 }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.25}
            sx={{ flex: 1, minWidth: 0 }}
          >
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: "0.08em", minWidth: 0 }}
            >
              Portfolio Graph
            </Typography>
            {/* (i) stays visible even when the chart body is collapsed —
             *  the glossary tooltip about the chart is still useful
             *  while the chart is hidden. */}
            <MetricTip term={TERMS.portfolioVsSpy}>
              <IconButton
                size="small"
                sx={{ p: 0.25, color: "text.secondary" }}
                aria-label="About this chart"
              >
                <InfoOutlined sx={{ fontSize: 14 }} />
              </IconButton>
            </MetricTip>
          </Stack>
          {!hidden && (
            // Round-7: clicking any part flips the mode — see the V2
            // toggle below for the full rationale. Applies here too
            // so V1 and V2 behave identically.
            <ToggleButtonGroup
              size="small"
              exclusive
              value={mode}
              onChange={(_, v) =>
                setMode((v as Mode | null) ?? (mode === "usd" ? "pct" : "usd"))
              }
              sx={{
                height: 28,
                "& .MuiToggleButton-root": {
                  px: 1.25, py: 0, fontWeight: 700, fontSize: "0.75rem",
                  border: 1, borderColor: "divider"
                }
              }}
            >
              <ToggleButton value="usd" aria-label="Dollar mode">$</ToggleButton>
              <ToggleButton value="pct" aria-label="Percent mode">%</ToggleButton>
            </ToggleButtonGroup>
          )}
          <IconButton
            size="small"
            aria-label={hidden ? "Show chart" : "Hide chart"}
            onClick={() => setHidden((v) => !v)}
            sx={{ p: 0.25, color: "text.secondary" }}
          >
            {hidden
              ? <VisibilityRounded sx={{ fontSize: 18 }} />
              : <VisibilityOffRounded sx={{ fontSize: 18 }} />}
          </IconButton>
        </Stack>
      )}

      {/* Everything below the header collapses when `hidden`. V2 never
       *  triggers `hidden` internally — the page gates the whole chart
       *  above this component, so the `!hidden &&` guard below is only
       *  load-bearing for V1. */}
      {!hidden && <>
      {/* Legend chips — left-aligned, wrap naturally onto multiple
       *  rows when the viewport is narrow. Chips are ordered so the
       *  widest one (Target, which includes a percent + gear icon)
       *  renders last and is the first to wrap onto a second line on
       *  mobile, keeping Portfolio · SPY · Cashflow together on row 1. */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        useFlexGap
        sx={{
          // V2 runs the hero → legend row → chart as one tight stack per
          // round-4 feedback ("should stay closer, one line just extended
          // to the second line"). V1 keeps the roomier 8px breathing room.
          mb: v2 ? 0.25 : 1,
          flexWrap: "wrap",
          rowGap: v2 ? 0.25 : 0.75,
          justifyContent: "flex-start",
          // Round-6: legend chips ride the same 16px gutter as the
          // hero heading — without this they butted against the screen
          // edge (the chart block has `mx: -2` on the page for full
          // bleed of the SVG) and no longer lined up with "$9,860.69"
          // above them. Graph body stays flush; only the chip rails
          // carry this gutter back.
          ...(v2 && { px: 2 })
        }}
      >
        {/* V1 keeps the Portfolio "P" chip. V2 hides it — the parent page
         *  renders a % change chip right under the total portfolio value
         *  heading, so a duplicate here is noise. The $ value is the one
         *  heading above us, and the line colour (violet) is still the
         *  chart's primary. */}
        {!v2 && (
          <LegendChip
            color={COLORS.portfolio}
            label={portLabel}
            value={portValue}
            // Compare against the DISPLAY curve's start so the up/down
            // colour matches the visible line (which now includes
            // cashflow layering in $ mode).
            valuePositive={isUsd ? usdAt >= (usdDisplay[0] ?? 0) : portAt >= 0}
            term={isUsd ? TERMS.portfolioValue : TERMS.portfolioTwr}
          />
        )}
        {/* SPY — chip is visible whenever `enabledSpy` is on (controlled
         *  by the Graph Settings switch). Click toggles `activeSpy`,
         *  which turns the line off but keeps the chip in the strip
         *  (dimmed). Re-click brings the line back. */}
        {enabledSpy && (
          <LegendChip
            color={COLORS.spy}
            label="SPY"
            value={spyValue}
            valuePositive={isUsd ? spyUsdAt >= (usdDisplay[0] ?? 0) : spyAt >= 0}
            active={activeSpy}
            onClick={() => toggleLine("spy")}
            term={TERMS.spyPrice}
            size={v2 ? "md" : "sm"}
          />
        )}
        {/* Cashflow chip — V1 only. Legend for the cyan (deposit) +
         *  red (withdrawal) dots on the chart. Click toggles their
         *  visibility. When the crosshair is directly on a cashflow
         *  date the chip's value slot shows the amount, signed and
         *  colored by type. Compact label is "Cash".
         *
         *  V2 hides this chip — cashflow amount appears inline with
         *  the date label instead (and only when non-zero), and the
         *  dots are always visible. */}
        {!v2 && enabledCashflow && (
          <LegendChip
            color={COLORS.deposit}
            secondaryColor={COLORS.withdrawal}
            label={cashflowLabel}
            value={cashflowValue}
            valuePositive={cashflowPositive}
            active={activeCashflow}
            onClick={() => toggleLine("cashflow")}
            term={{
              title: "Cashflow",
              body: "Deposits (cyan) and withdrawals (red) at the dates they hit the account. The dot at index 0 represents the opening balance. Click the chip to show or hide these markers; hover over a dot to see the amount."
            }}
          />
        )}
        {/* Target chip — rendered LAST of the "row-1" group so it's
         *  the first to wrap onto a new row on narrow viewports.
         *  Carries the widest label (includes target % + gear icon),
         *  which is why it gets a line of its own on mobile while
         *  P · SPY · Cash stay on row 1. Click toggles the line; gear
         *  opens the editor.
         *
         *  Chip is always visible; `active` toggles dim / bright. The
         *  gear icon is still the entry point for the APY editor. */}
        {enabledTarget && (
          <LegendChip
            color={COLORS.target}
            label={targetLabel}
            value={targetValue}
            valuePositive={isUsd ? targetUsdAt >= (usdDisplay[0] ?? 0) : targetPctAt >= 0}
            active={activeTarget}
            onClick={() => toggleLine("target")}
            // Apr-2026 V2 icon-size sweep: gear was 12px — looked like a
            // fleck of dust next to the 24px range-row icons. Bumped so it
            // carries the same visual weight as the calendar / rebase /
            // $-% controls the user sees just above the chip row.
            actionIcon={<SettingsOutlined sx={{ fontSize: v2 ? 20 : 12 }} />}
            actionLabel="Edit target APY"
            onAction={editTargetPct}
            term={{
              title: `APY Target (${targetPct}%)`,
              body: isUsd
                ? "Annual compounding return target. In $ mode the line compounds the starting value plus every deposit at this rate. Click the gear to change it."
                : `Annual compounding return target. Click the gear to change it.`
            }}
            size={v2 ? "md" : "sm"}
          />
        )}
        {/* SPY + Dividends (total return) chip — works in BOTH modes
         *  and is always visible. Reads from `spyTotalCurve` in mock-
         *  data, the same ADJ-style series that drives the WHB
         *  weekly report (received dividends reinvested at the close
         *  of each dividend date). Click toggles on/off; chip stays
         *  in the hero either way. */}
        {enabledSpyDiv && (
          <LegendChip
            color={COLORS.spyDiv}
            label="SPY+D"
            value={spyDivValue}
            valuePositive={isUsd ? spyDivAt >= (usdDisplay[0] ?? 0) : spyDivAt >= 0}
            active={activeSpyDiv}
            onClick={() => toggleLine("spyDiv")}
            term={{
              title: "SPY + Dividends",
              body: "SPY total return — price change with every received dividend reinvested at the close of the dividend date (ADJ-style). In % mode this is the total-return percentage; in $ mode it's what your opening balance would be worth if invested in SPY with dividends reinvested. Compare against Portfolio to see whether you're beating a straight SPY total-return index."
            }}
            size={v2 ? "md" : "sm"}
          />
        )}
        {/* Net Deposits — stepped "capital held" line. $-mode only per
         *  Round 10: the line shows the running total of deposits minus
         *  withdrawals and is meaningless in % mode. Always visible in
         *  $ mode; click toggles the line's dim / bright state. */}
        {isUsd && enabledNetDeposits && (
          <LegendChip
            color={COLORS.netDeposit}
            label={netDepositLabel}
            value={netDepositValue}
            valuePositive={netDepositPositive}
            active={activeNetDeposits}
            onClick={() => toggleLine("netDeposits")}
            term={{
              title: "Net Deposits",
              body: "Running total of your real cashflows layered on the window's opening balance: starts at your opening balance and steps up/down whenever a deposit or withdrawal lands. Flat between cashflow events. Useful as a 'capital-held' reference line you can compare Portfolio against."
            }}
            size={v2 ? "md" : "sm"}
          />
        )}
        {/* SMA chip — trailing simple moving average over the displayed
         *  Portfolio line. Window auto-picks per range or honours the
         *  user's fixed override. Gear opens the picker dialog so the
         *  user can switch between Auto / preset / Custom. Sergey
         *  2026-04-30. */}
        {enabledSma && smaWindow >= 2 && (
          <LegendChip
            color={COLORS.sma}
            label={`SMA(${smaWindow}d)`}
            value={smaAt == null ? "—" : isUsd ? fmtUsd(smaAt) : fmtPctSigned(smaAt)}
            valuePositive={
              isUsd
                ? (smaAt ?? 0) >= (usdDisplay[0] ?? 0)
                : (smaAt ?? 0) >= 0
            }
            active={activeSma}
            onClick={() => toggleLine("sma")}
            actionIcon={<SettingsOutlined sx={{ fontSize: v2 ? 20 : 12 }} />}
            actionLabel="Edit SMA window"
            onAction={() => setSmaDialogOpen(true)}
            term={{
              title: `Simple moving average (${smaWindow}-day)`,
              body:
                smaWindowMode === "auto"
                  ? `Trailing ${smaWindow}-day average of the Portfolio line, picked automatically for this range. Tap the gear to fix a window across all ranges or pick a custom value.`
                  : `Trailing ${smaWindow}-day average of the Portfolio line — fixed for every range. Tap the gear to switch to Auto or pick a different window.`
            }}
            size={v2 ? "md" : "sm"}
          />
        )}
      </Stack>

      {/* Date label — the only tooltip that remains. Centered below the
       *  chip row, fixed height (nbsp fallback) so the layout doesn't
       *  jump when the user moves off the chart.
       *
       *  V2 appends the cashflow amount inline (e.g. "Fri, Sep 5, 2025
       *  · +$2,500") when the crosshair is directly over a deposit or
       *  withdrawal dot — replaces the V1 Cashflow legend chip, which
       *  V2 has dropped. Synthetic (opening-balance) markers are
       *  excluded so the starting-balance dot doesn't flash a huge
       *  number every time the user hits the left edge. */}
      {/* R12-12: Wrap the date label in a flex row so we can append a
       *  small × dismiss button next to the locked date without
       *  breaking horizontal centering. Button only renders when the
       *  crosshair is locked AND the user isn't actively hovering
       *  (hover wins — the × would compete with scrubbing). */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.75,
          minHeight: "1.25em",
          mb: 0.75
        }}
      >
      <Typography
        // Apr-2026 V2: when a specific date is being hovered, the
        // centered date label is THE read for the user, so bump it to
        // body1 (matches Buying Power / delta chip weight). When no
        // hover is active we still want the &nbsp; fallback to hold
        // layout but can stay small — so V2 switches variant based on
        // hover state, V1 keeps the original caption.
        // R12-11: Selected Range (when ON + not hovering) is also a
        // "real date" read — bump it to body1 so "May 20, 2024 – Apr
        // 20, 2026" matches the hover-date typography instead of
        // shrinking to caption.
        // R12-12: locked date is also a "real date" read — same bump.
        variant={
          v2 && (
            hoverIdx != null
            || lockedIdx != null
            || (showSelectedRange && dateObjs.length > 0)
          )
            ? "body1"
            : "caption"
        }
        color="text.secondary"
        sx={{
          textAlign: "center",
          fontFeatureSettings: '"tnum" 1'
        }}
      >
        {lockedIdx != null && dateObjs[lockedIdx]
          /* R12-12 refined: Locked wins over hover. Once a date is
           *  pinned, the centered label stays on that date even as the
           *  user keeps hovering/moving — matches the pinned-crosshair
           *  mental model. Cashflow inline suffix is tied to live
           *  hover and intentionally omitted in the lock branch. */
          ? dateObjs[lockedIdx]!.toLocaleDateString("en-US", {
              weekday: "short",
              year: "numeric",
              month: "short",
              day: "numeric"
            })
          : hoverIdx != null && dateObjs[hoverIdx] ? (
          <>
            {dateObjs[hoverIdx]!.toLocaleDateString("en-US", {
              weekday: "short",
              year: "numeric",
              month: "short",
              day: "numeric"
            })}
            {v2
              && cashflowAtHover
              && cashflowAtHover.amount !== 0
              && cashflowValue && (
                <>
                  {" · "}
                  {/* "Cash" label prefix per round-5 feedback — without
                   *  it the signed amount was ambiguous (is that a price
                   *  delta? portfolio change?). Kept in the same muted
                   *  grey as the date so the amount itself still pops
                   *  in deposit-cyan / withdrawal-red. */}
                  <Box component="span" sx={{ color: "text.secondary" }}>
                    Cash{" "}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 700,
                      color: cashflowAtHover.amount >= 0
                        ? COLORS.deposit
                        : COLORS.withdrawal
                    }}
                  >
                    {cashflowValue}
                  </Box>
                </>
            )}
          </>
        ) : showSelectedRange && dateObjs.length > 0
              /* R12-11: "Selected Range" toggle — when ON and the user
               *  is NOT hovering a specific date, fill the slot with
               *  the actual calendar window ("May 20, 2024 – April 20,
               *  2026") so the persistent reminder carries the concrete
               *  dates rather than a prose label. Format matches the
               *  hover date's long form so the slot reads consistently
               *  whether the user is hovering or not. */
              ? `${dateObjs[0]!.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric"
                })} – ${dateObjs[dateObjs.length - 1]!.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric"
                })}`
              : (range === "CUSTOM" && customRange)
                  /* CUSTOM + not hovering: show the selected window
                   *  ("Apr 3 – Jul 17") in this slot per Apr-2026
                   *  feedback even when Selected Range is OFF — custom
                   *  ranges have no other persistent indicator once the
                   *  chip scrolls off-screen, so they're the one case
                   *  where we always surface the dates. */
                  ? formatCustomRangeLabel(customRange)
                  : "\u00A0" /* non-breaking space — reserve vertical space */ }
      </Typography>
      {/* R12-12 refined: × dismiss for the locked crosshair. Lock
       *  wins over hover, so the × is shown whenever a lock exists —
       *  regardless of whether the user is currently hovering. The
       *  user must click this (or hit Escape / change range / change
       *  mode / switch account) to clear the lock and re-enable
       *  hover. Small caption-sized button inline with the date. */}
      {lockedIdx != null && (
        <IconButton
          size="small"
          aria-label="Dismiss pinned date"
          onClick={() => setLockedIdx(null)}
          sx={{
            p: 0.25,
            // Visually secondary — we don't want it to compete with the
            // date; the user glances at it when they're done inspecting.
            color: "text.secondary",
            "&:hover": { color: "text.primary" }
          }}
        >
          <CloseIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      )}
      </Box>

      {/* Chart — axes hidden, crosshair + hollow-dot highlights on hover.
       *  `touchAction: "pan-y"` lets vertical swipes that start inside
       *  the chart flow through to the browser as page scroll, so the
       *  chart doesn't trap scroll on mobile. Horizontal drags and taps
       *  are still captured by MUI X Charts for the crosshair, so the
       *  reactive chips + date label still work as before. The `*` child
       *  selector forces the rule onto the SVG `<ChartsSurface>` that
       *  lives inside `ResponsiveChartContainer` — without it, the SVG's
       *  own `touch-action: none` would override the container.
       *
       *  Mobile height reduced from 220 → 180 so the card takes less of
       *  the viewport and the page is skimmable. Width is unchanged. */}
      {/* R12-12 (fix 3): Tap-to-lock via native pointerdown + geometry.
       *
       *  Earlier attempts relied on (a) an outer `onClick` with a ref
       *  mirror of `hoverIdx`, and (b) MUI's `ChartsOnAxisClickHandler`.
       *  Both failed on this chart — (a) because MUI clears the axis
       *  interaction state on pointerup before click fires, and also
       *  because the DOM click event doesn't reliably bubble out of the
       *  chart's SVG tree in all configurations; (b) because
       *  `ChartsOnAxisClickHandler` reads the same interaction state
       *  internally and has the same pointerup-clears-before-click
       *  race.
       *
       *  The bulletproof fix: attach a native `pointerdown` listener to
       *  the wrapper Box (capture phase so nothing downstream can
       *  swallow it), measure the pointer's X relative to the Box, and
       *  map it to the nearest dateObjs index using the same xMin/xMax
       *  padding the chart uses. No reliance on MUI's tooltip or axis
       *  interaction state, no ref-mirror timing games. Fires on the
       *  first touch of a tap and on the mousedown of a click, so it
       *  works identically on mobile and desktop. */}
      <Box
        ref={chartWrapperRef}
        // Edge-hover fallback. Engages ONLY in the outer band at each
        // end of the plot — everywhere else `edgeHoverIdx` is null and
        // MUI's own axis hover drives the crosshair untouched.
        //
        // Band width is pointer-type aware:
        //   • mouse — the 10px margin gutter + a 2px buffer. Just wide
        //     enough to grab the endpoint without stealing precision
        //     from the interior indices next to it.
        //   • touch — MARGIN + 14 ≈ 24px, matching the constant the
        //     touch pointerup fallback already uses (wider than a
        //     fingertip; on a dense ALL range each interior index is
        //     sub-2px, so a finger near the end unambiguously means
        //     "the last point").
        onPointerMove={(e) => {
          setEdgeHoverIdx(
            pointerIndex(
              e.clientX,
              e.currentTarget.getBoundingClientRect(),
              dateObjs.length,
              e.pointerType,
              // edges only — the interior stays MUI's job
              true
            )
          );
        }}
        onPointerLeave={() => setEdgeHoverIdx(null)}
        onPointerCancel={() => setEdgeHoverIdx(null)}
        onPointerDownCapture={(e) => {
          // Capture touch clientX immediately so a brief tap (no
          // pointermove between down and up) still has a coordinate
          // for the edge-tap geometry fallback in the pointerup
          // listener.
          if (e.pointerType === "touch") {
            lastTouchClientXRef.current = e.clientX;
            // New gesture → reset the hover-seen flag for mobile auto-
            // lock. A touch that never triggers a hoverIdx update
            // (e.g. a very brief tap MUI doesn't register) won't
            // auto-lock unless the geometry fallback supplies one.
            hoverSeenInTouchRef.current = false;
          }
          // Desktop explicit click-to-lock. On touch the lock is set
          // via the window-level pointerup listener above (reading
          // lastHoverIdxRef + edge-tap geometry fallback), so we skip
          // pointerdown entirely on touch. While already locked,
          // ignore — user must clear with × / Escape / range change
          // first per the "click once, inspect, clear, click again"
          // model.
          if (e.pointerType !== "mouse") return;
          if (lockedIdx != null) return;
          // Shared geometry with the hover fallback + touch pointerup
          // path, so a click lands on the same index the crosshair was
          // showing — including the edge-snapped endpoints.
          const nearest = pointerIndex(
            e.clientX,
            e.currentTarget.getBoundingClientRect(),
            dateObjs.length,
            e.pointerType
          );
          if (nearest != null) setLockedIdx(nearest);
        }}
        sx={{
          // No negative pull. The page wrapper already bleeds this
          // block edge-to-edge (`mx: -2` cancels the page's `px: 2`);
          // the extra `mx: -1` this Box used to carry pushed the SVG a
          // further 8px OUTSIDE the visible card, which is what clipped
          // the first/last crosshair markers into half-moons. The chart
          // still spans the full card width — the endpoints just sit
          // CHART_MARGIN_PX inside it so their dots render whole
          // (Sergey 2026-08-03).
          mx: 0,
          position: "relative",
          touchAction: "pan-y",
          cursor: "pointer",
          // R12-12 (polish): kill the mobile tap-flash. Browsers paint a
          // translucent highlight over any element a touch lands on when
          // that element looks clickable (pointer cursor, pointerdown
          // handler). On our chart that shows up as a grey rectangle
          // flashing over the plot area on every tap-to-lock. Setting
          // WebkitTapHighlightColor + outline:none + user-select:none on
          // the wrapper AND descendants (the SVG tree) suppresses it on
          // iOS Safari and Android Chrome.
          WebkitTapHighlightColor: "transparent",
          outline: "none",
          userSelect: "none",
          WebkitUserSelect: "none",
          "& *": {
            WebkitTapHighlightColor: "transparent",
            outline: "none"
          },
          "& > div, & svg": { touchAction: "pan-y !important" }
        }}
      >
        <ResponsiveChartContainer
          height={v2 ? 220 : compact ? 180 : 300}
          // Horizontal margin is `CHART_MARGIN_PX` — see that constant
          // for why it's 16 and why the earlier 6/8/10 bumps never
          // fixed the clipped endpoint markers. Every pointer→index
          // calculation reads the same constant, so they can't drift
          // apart again.
          //
          // Top/bottom stay at 6: `isPointInside` gates hover on the Y
          // axis too, so a large vertical margin would create dead
          // bands at the top and bottom of the plot.
          margin={{
            top: 6,
            bottom: 6,
            left: CHART_MARGIN_PX,
            right: CHART_MARGIN_PX
          }}
          xAxis={[{
            id: "x",
            data: dateObjs,
            // Point (categorical) scale — every entry in `dateObjs` gets
            // equal x-spacing, with no gaps for weekends or NYSE
            // holidays. Switched from "time" 2026-04-24 because the
            // continuous time axis kept rendering Sat/Sun positions
            // (the crosshair could lock on "Sun, Apr 19, 2026" even
            // though there was no data point there) and Fri→Mon gaps
            // looked like missing trading days. Categorical scale also
            // means scatter dot `x` values must match an entry in
            // `dateObjs` by reference (we pass `dateObjs[xIdx]`
            // directly below), and `min`/`max` time bounds no longer
            // apply — MUI auto-fits the categorical range.
            scaleType: "point",
            // Context-aware formatter: tooltip gets the full weekday +
            // year; ticks get the short form (which is no longer rendered
            // since we hide the axis, but kept for future re-enable).
            valueFormatter: (v: Date, ctx?: { location: string }) =>
              ctx?.location === "tooltip"
                ? v.toLocaleDateString("en-US", {
                    weekday: "short",
                    year: "numeric",
                    month: "short",
                    day: "numeric"
                  })
                : v.toLocaleDateString("en-US", { month: "short", day: "numeric" })
          }]}
          yAxis={[{
            id: "y",
            // V2 passes explicit min/max to eliminate auto-padding above
            // and below the lines. V1 leaves these undefined so the
            // default padded scale is preserved.
            ...(yDomain.min !== undefined && yDomain.max !== undefined
              ? { min: yDomain.min, max: yDomain.max }
              : {}),
            valueFormatter: (v: number) =>
              isUsd ? fmtUsd(v) : fmtPctSigned(v, 2)
          }]}
          series={[
            // Main portfolio series — TWR in % mode, raw USD in $ mode.
            // In % mode we use `twrDisplay` (rebased when the user has
            // toggled Rebase on, otherwise == sliced.twr).
            {
              type: "line",
              label: isUsd ? "Portfolio Value" : "Portfolio TWR",
              // `usdDisplay` = market walk + layered cashflows, so the
              // line steps up on deposit days and down on withdrawal
              // days — matching user intuition for "portfolio value"
              // in $ mode. `twrDisplay` stays cashflow-agnostic in %
              // mode (TWR is time-weighted by design).
              data: isUsd ? usdDisplay : twrDisplay,
              color: COLORS.portfolio,
              showMark: false,
              area: true,
              curve: "monotoneX",
              valueFormatter: (v) =>
                v == null ? "" : isUsd ? fmtUsd(v) : fmtPctSigned(v)
            },
            // SMA overlay — trailing simple moving average over the
            // displayed line. Rendered AFTER Portfolio so its
            // desaturated tone reads as a smoothed trail riding the
            // live line. Self-hides when the curve is empty (window <
            // smaWindow points). Sergey 2026-04-30.
            ...(showSma && smaCurve.length > 0 ? [{
              id: "sma",
              type: "line" as const,
              label: `SMA(${smaWindow})`,
              data: smaCurve,
              color: COLORS.sma,
              showMark: false,
              curve: "monotoneX" as const,
              valueFormatter: (v: number | null) =>
                v == null ? "" : isUsd ? fmtUsd(v) : fmtPctSigned(v)
            }] : []),
            // SPY comparison — works in BOTH modes per Apr-2026
            // feedback. In % mode it's the rebased SPY % change; in $
            // mode it's the opening portfolio balance scaled by the
            // SPY relative price factor (i.e. "what would your starting
            // dollars be worth tracking SPY"). Same Rebase treatment
            // as Portfolio in % mode; $ mode needs no rebase because
            // it's always anchored to the opening balance.
            ...(showSpy ? [{
              type: "line" as const,
              label: "SPY",
              data: isUsd ? spyUsdCurve : spyDisplay,
              color: COLORS.spy,
              showMark: false,
              curve: "monotoneX" as const,
              valueFormatter: (v: number | null) =>
                v == null ? "" : isUsd ? fmtUsd(v) : fmtPctSigned(v)
            }] : []),
            // Target line — compounding APY curve in BOTH modes.
            // `id: "target"` lets us dash this series via sx
            // (.MuiLineElement-series-target) without affecting the
            // Portfolio or SPY lines.
            ...(showTarget ? [
              isUsd
                ? {
                    id: "target",
                    type: "line" as const,
                    label: `Target (${targetPct}%)`,
                    data: usdTargetCurve,
                    color: COLORS.target,
                    showMark: false,
                    curve: "linear" as const,
                    valueFormatter: (v: number | null) => v == null ? "" : fmtUsd(v)
                  }
                : {
                    id: "target",
                    type: "line" as const,
                    label: `Target (${targetPct}%)`,
                    data: pctTargetCurve,
                    color: COLORS.target,
                    showMark: false,
                    curve: "linear" as const,
                    valueFormatter: (v: number | null) =>
                      v == null ? "" : `+${v.toFixed(2)}%`
                  }
            ] : []),
            // Net Deposits — stepped horizontal line ($-mode only) that
            // changes only at deposit/withdrawal events. Flat between
            // cashflows, jumps vertically on each event. stepAfter
            // curve gives the correct visual "capital held" shape.
            ...(isUsd && showNetDeposits && netDepositsCurve.length > 0 ? [{
              id: "netDeposits",
              type: "line" as const,
              label: "Net Deposits",
              data: netDepositsCurve,
              color: COLORS.netDeposit,
              showMark: false,
              curve: "stepAfter" as const,
              valueFormatter: (v: number | null) =>
                v == null ? "" : fmtUsd(v)
            }] : []),
            // "SPY + Dividends" — SPY total return (price + reinvested
            // dividends at ~1.8% annualised). Works in BOTH modes:
            // % mode plots the combined total-return percentage,
            // $ mode plots opening balance × (1 + totalReturn%/100).
            // Amber, solid, smooth — kin to the SPY orange but
            // distinct enough to read alongside it.
            ...(showSpyDiv && spyDivCurve.length > 0 ? [{
              id: "spyDividends",
              type: "line" as const,
              label: "SPY + Dividends",
              data: spyDivCurve,
              color: COLORS.spyDiv,
              showMark: false,
              curve: "monotoneX" as const,
              valueFormatter: (v: number | null) =>
                v == null ? "" : isUsd ? fmtUsd(v) : `+${v.toFixed(2)}%`
            }] : []),
            // Cashflow dots — deposits cyan, withdrawals red. Split into
            // two scatter series so each gets its own colour while still
            // living in the same coordinate space as the line plot.
            // Round 7 removed the floating tooltip entirely, so the
            // scatter `valueFormatter` never fires — the dots are now
            // pure visual markers. Kept defined for a future hover-card
            // that might list cashflows at the active index.
            // Cast x through `unknown as number` because MUI X's
            // `ScatterValueType.x` types it strictly as `number`, but
            // at runtime the value is forwarded to the d3 scale whose
            // domain here is the Date[] array driving the categorical
            // xAxis. Reference equality with `dateObjs[xIdx]` is what
            // anchors each marker to a point on the line — passing a
            // plain number doesn't match the Date domain and the
            // scatter renders zero circles. Sergey 2026-04-30: DOM
            // probe confirmed series=5 (3 line + 2 scatter) but
            // circles=0 until this cast was introduced.
            ...(showCashflow && cashflowScatter.filter((p) => p.cf.type === "deposit").length > 0 ? [{
              type: "scatter" as const,
              label: "Deposits",
              data: cashflowScatter
                .filter((p) => p.cf.type === "deposit")
                .map((p, i) => ({
                  x: p.x as unknown as number,
                  y: p.y,
                  id: `dep-${i}`
                })),
              markerSize: 6,
              color: COLORS.deposit,
              valueFormatter: (pt: { x: number; y: number; id: string | number } | null) => {
                if (!pt) return "";
                const amt = cashflowScatter
                  .filter((p) => p.cf.type === "deposit")[Number(String(pt.id).split("-")[1] ?? 0)]?.cf.amount;
                return amt != null ? `+${fmtUsd(Math.abs(amt))}` : "";
              }
            }] : []),
            ...(showCashflow && cashflowScatter.filter((p) => p.cf.type === "withdrawal").length > 0 ? [{
              type: "scatter" as const,
              label: "Withdrawals",
              data: cashflowScatter
                .filter((p) => p.cf.type === "withdrawal")
                .map((p, i) => ({
                  x: p.x as unknown as number,
                  y: p.y,
                  id: `wd-${i}`
                })),
              markerSize: 6,
              color: COLORS.withdrawal,
              valueFormatter: (pt: { x: number; y: number; id: string | number } | null) => {
                if (!pt) return "";
                const amt = cashflowScatter
                  .filter((p) => p.cf.type === "withdrawal")[Number(String(pt.id).split("-")[1] ?? 0)]?.cf.amount;
                return amt != null ? `−${fmtUsd(Math.abs(amt))}` : "";
              }
            }] : [])
            // R12-12 (rollback 2026-04-30): the always-on edge-marker
            // scatter at index 0 / N-1 was reverted because it broke
            // drag-to-scrub on mobile — MUI's useAxisTooltip stopped
            // updating the chips during swipe and the chart background
            // would render a black overlay. The visual orientation
            // benefit isn't worth the regression. Edge tap-ability is
            // preserved by the pointerup geometry fallback below
            // (touch path) and by the wider chart margin (10px) that
            // moves boundary indices into the visible card area.
          ]}
          sx={{
            // Hide the axis lines, ticks, and tick labels completely so
            // the plot area uses every pixel. The scales still work —
            // we just don't render the visual axis glyphs.
            "& .MuiChartsAxis-root .MuiChartsAxis-line": { display: "none" },
            "& .MuiChartsAxis-root .MuiChartsAxis-tick": { display: "none" },
            "& .MuiChartsAxis-root .MuiChartsAxis-tickLabel": { display: "none" },
            // Crosshair lines (dashed gray, follow cursor).
            "& .MuiChartsAxisHighlight-root": {
              stroke: alpha(theme.palette.text.primary, 0.35),
              strokeWidth: 1,
              strokeDasharray: "3 3"
            },
            // Hollow hover-dots: see the custom `slots.lineHighlight`
            // below. We don't use a CSS override here because the
            // default inline `fill={seriesColor}` can't be converted to
            // a *stroke* via CSS alone — we need the slot to render a
            // circle with fill=bg + stroke=seriesColor.
            //
            // Target line is dashed in both $ and % mode. Targeting by
            // the series id keeps Portfolio + SPY lines solid.
            "& .MuiLineElement-series-target": {
              strokeDasharray: "4 4"
            },
            // SMA — thinner stroke than Portfolio (2px → 1.25px) so the
            // smoothed line reads as a secondary trend overlay instead
            // of competing with the live Portfolio line for visual
            // weight. Sergey 2026-04-30.
            "& .MuiLineElement-series-sma": {
              strokeWidth: 1.25
            },
            "& .MuiAreaElement-root": { fill: "url(#portfolioTwrFill)" }
          }}
        >
          <AreaPlot />
          <LinePlot />
          <MarkPlot />
          <ScatterPlot />
          {/* Hover layer — crosshair + per-line hollow donut dots.
           *  Order matters: ChartsAxisHighlight draws *underneath* the
           *  dots so the dots sit on top.
           *
           *  R12-12 refined: when a date is locked, suppress MUI's
           *  hover crosshair entirely. Lock wins over hover, so the
           *  live hover lines would otherwise drift around on top of
           *  our pinned dashed overlay. Our own overlay (rendered
           *  below the SVG gradient defs) takes over while locked. */}
          {/* R12-12 refined (hover edition):
           *  - Vertical crosshair on HOVER: ChartsAxisHighlight x="line"
           *    — MUI snaps it to the nearest x-axis data point.
           *  - Horizontal crosshair on HOVER: y="line" was following the
           *    cursor's raw y — that's the bug the user screenshotted.
           *    Replaced with a
           *    ChartsReferenceLine at y = Portfolio[hoverIdx] so the
           *    horizontal ALWAYS snaps to the Portfolio line.
           *  - While LOCKED: both vertical + horizontal come from
           *    ChartsReferenceLine at the locked index (ChartsAxisHighlight
           *    suppressed so live hover doesn't override).
           */}
          {lockedIdx == null && <ChartsAxisHighlight x="line" />}
          <LineHighlightPlot
            slots={{
              lineHighlight: (props) => (
                <HollowHighlight
                  {...props}
                  paperColor={theme.palette.background.paper}
                />
              )
            }}
          />
          {(() => {
            const portfolioSeries = isUsd ? usdDisplay : twrDisplay;
            // Lock wins over hover; otherwise follow hover.
            const effectiveIdx =
              lockedIdx != null
                ? lockedIdx
                : hoverIdx != null && hoverIdx >= 0 && hoverIdx < dateObjs.length
                  ? hoverIdx
                  : null;
            if (effectiveIdx == null || !dateObjs[effectiveIdx]) return null;
            const yValue = portfolioSeries[effectiveIdx];
            const strokeStyle = {
              stroke: alpha(theme.palette.text.primary, 0.55),
              strokeDasharray: "3 3",
              strokeWidth: 1
            };
            return (
              <>
                {/* Vertical: only rendered when LOCKED, because
                 *  ChartsAxisHighlight already handles the hover case.
                 *  Adding another vertical on hover would double-draw. */}
                {lockedIdx != null && (
                  <ChartsReferenceLine
                    axisId="x"
                    x={dateObjs[effectiveIdx]!}
                    lineStyle={strokeStyle}
                  />
                )}
                {/* Horizontal: ALWAYS from ChartsReferenceLine at the
                 *  Portfolio's y-value, so on both hover and lock it
                 *  snaps to the Portfolio line — not the cursor's
                 *  raw y. Explicit axisId guarantees use of the same
                 *  y-scale as the Portfolio series. */}
                {yValue != null && Number.isFinite(yValue) && (
                  <ChartsReferenceLine
                    axisId="y"
                    y={yValue}
                    lineStyle={strokeStyle}
                  />
                )}
              </>
            );
          })()}
          {/* HoverTracker has no visual output — it just subscribes to
           *  the axis tooltip state via `useAxisTooltip()` and pipes
           *  the current x-index back up to this component through
           *  `setHoverIdx`. That drives reactive chip values and the
           *  centered date label above the chart. No popper, no
           *  floating tooltip. */}
          <HoverTracker onChange={setMuiHoverIdx} />
        </ResponsiveChartContainer>
        {/* Gradient def for the portfolio area fill */}
        <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden>
          <defs>
            <linearGradient id="portfolioTwrFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={alpha(COLORS.portfolio, 0.35)} />
              <stop offset="100%" stopColor={alpha(COLORS.portfolio, 0)} />
            </linearGradient>
          </defs>
        </svg>
        {/* R12-12 refined: Locked crosshair lives INSIDE the chart now
         *  (ChartsReferenceLine + ChartsReferenceLine above). MUI
         *  renders them through its own scale so they land pixel-
         *  perfect on the Portfolio path. No DOM overlay needed. */}
      </Box>

      {/* Range chips — moved UNDER the chart per Sergey's Apr-17 v2 spec.
       *  Centered + wrapping in V1 so they read as a compact pill rail.
       *
       *  V2 splits the row in two: a scrollable chip rail on the left
       *  (presets + customRange) and a *pinned* control group on the
       *  right (calendar, rebase, $/% toggle). Before round 4 the whole
       *  row scrolled, which meant flicking sideways could push the
       *  most-important controls (especially $/%) off-screen — Sergey
       *  wanted those three to stay anchored so users never lose sight
       *  of the mode switch or the custom-range picker. "W" (7d) is
       *  prepended to the preset list in V2 — stock apps like Robinhood
       *  lead with a one-week view, so V2 follows that convention. */}
      {v2 ? (
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            mt: 1.5,
            // Apr-2026: bumped from 0.5 → 1.5 (12px) so the pinned
            // right-side group (Rebase + $/%) doesn't crowd against
            // the "All" chip — they read as one jumble otherwise.
            gap: 1.5,
            // Round-6: match the hero's 16px gutter so the chip rail
            // + pinned controls line up vertically with "$9,860.69"
            // and the legend chips above. The chart graphics are still
            // full-bleed (`mx: -2` on the page wrapper); only this
            // control rail gets the gutter back, so the "W" chip and
            // "%" toggle no longer touch the screen edges.
            px: 2
          }}
        >

          {/* Scrollable chip rail. `flex: 1` so it consumes remaining
           *  space; `minWidth: 0` so the inner overflow actually takes
           *  effect (without it flexbox would happily stretch the row
           *  to fit all chips, defeating overflow-x). `rangeRailRef`
           *  lets us programmatically center the active chip on tap
           *  (see `centerRangeChip` above).
           *
           *  Inner Stack uses `spacing={1}` (8px gutter) instead of
           *  the old 0.5 (4px) so the larger body1-sized chips don't
           *  feel crammed into each other — matches Robinhood's breath
           *  between range buttons. */}
          <Box
            ref={rangeRailRef}
            sx={{
              flex: 1,
              minWidth: 0,
              overflowX: "auto",
              overflowY: "hidden",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" }
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ "& > *": { flexShrink: 0 } }}
            >
              {PRESET_RANGES_V2.map((k) => (
                <Chip
                  key={k}
                  data-range-key={k}
                  label={k === "ALL" ? "All" : k}
                  size="small"
                  onClick={() => {
                    setRange(k);
                    // Auto-toggle rebase based on the range kind:
                    //   • ALL     → OFF (rebase is a no-op on ALL since
                    //     window-open = inception, and the toggle is
                    //     hidden there — don't leave stale state that
                    //     would surprise the user when they switch back).
                    //   • Non-ALL → ON (industry-standard default: users
                    //     expect the chart + chips to read as
                    //     "performance over the selected window", not
                    //     "cumulative since inception, clipped"). Matches
                    //     Sergey's Apr-2026 feedback "by default rebase
                    //     enabled for all ranged dates". User can still
                    //     flip it off via the rebase icon.
                    if (k === "ALL") setRebased(false);
                    else setRebased(true);
                    centerRangeChip(k);
                    // R12-12: Re-tapping the active range should clear
                    // the lock too (matches the user's "tap to reset
                    // this range" mental model). The state-change
                    // useEffect won't fire on same-value sets, so do it
                    // explicitly here.
                    setLockedIdx(null);
                  }}
                  variant={range === k ? "filled" : "outlined"}
                  color={range === k ? "primary" : "default"}
                  sx={[activeRangeChipSx, range === k && activeRangeChipActiveSx]}
                />
              ))}
              {customRange && (
                <Chip
                  data-range-key="CUSTOM"
                  label={formatCustomRangeLabel(customRange)}
                  size="small"
                  onClick={() => {
                    setRange("CUSTOM");
                    // Custom ranges are always windowed, so rebase ON is
                    // the right default (same policy as the named
                    // ranges above).
                    setRebased(true);
                    centerRangeChip("CUSTOM");
                    setLockedIdx(null);
                  }}
                  onDelete={() => {
                    setCustomRange(null);
                    if (range === "CUSTOM") setRange("ALL");
                  }}
                  variant={range === "CUSTOM" ? "filled" : "outlined"}
                  color={range === "CUSTOM" ? "primary" : "default"}
                  sx={[activeRangeChipSx, range === "CUSTOM" && activeRangeChipActiveSx]}
                />
              )}
              {/* Calendar — moved into the scrollable chip rail per
               *  round-7 feedback. Used to live in the pinned right
               *  group with rebase + $/%, but it's a range *picker*
               *  (same job as the W/1M/3M chips), so it belongs with
               *  them. The pinned group is now just mode-controls
               *  (rebase, $/%) — operations on the active range, not
               *  ways of choosing the range.
               *
               *  Apr-2026: icon size bumped to match the 36px chip
               *  height so the control doesn't read as a smaller
               *  "lesser" affordance next to the range chips it lives
               *  with. */}
              <IconButton
                size="small"
                aria-label="Pick custom date range"
                onClick={() => setPickerOpen(true)}
                sx={{
                  p: 0.5,
                  ml: 0.25,
                  color: range === "CUSTOM" ? "primary.main" : "text.secondary",
                  "&:hover": { color: "primary.main" }
                }}
              >
                <CalendarMonthRounded sx={{ fontSize: 24 }} />
              </IconButton>
            </Stack>
          </Box>
          {/* Pinned right-side controls — rebase (both modes) + Graph
           *  Settings gear. `flexShrink: 0` + parent gap keeps these
           *  bolted to the right edge even when the chip rail overflows.
           *
           *  The inline $/% toggle that used to live here moved OUT in
           *  the Apr-2026 "tap-the-delta-to-switch-mode" rework: mode
           *  switching now happens either by tapping the +$ / +% chips
           *  in the hero row above the chart, or from the View section
           *  of the Graph Settings dialog opened by this gear. This row
           *  is now just "things that modify the current range/view" —
           *  rebase + settings. */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={{ flexShrink: 0 }}
          >
            {/* Rebase — shifts every visible line to start at 0 (0% in
             *  % mode, $0 in $ mode) at the window's left edge so they
             *  can be compared on equal footing. Hidden on ALL range in
             *  both modes: ALL already is the full window, so "shift
             *  to start at zero" is either a no-op (% mode: the first
             *  value IS 0 by construction) or semantically meaningless
             *  ($ mode: there's no "starting balance" earlier to show
             *  PnL against). Available on every sub-range in both modes
             *  per Apr-2026 R11 feedback: "Where you got rebase on $
             *  graph?" — but hidden on ALL per follow-up feedback. */}
            {range !== "ALL" && (
              <Tooltip
                arrow
                placement="top"
                title={
                  rebased
                    ? `Rebase is ON. All lines have been shifted so they start at ${isUsd ? "$0" : "0%"} at the left edge of the window — in $ mode this reads as PnL since window start. Click to turn off and show absolute values.`
                    : `Rebase: shift every line so it starts at ${isUsd ? "$0" : "0%"} at the left edge of the window. Makes Portfolio, SPY, SPY+D, Target and ND directly comparable on equal footing.`
                }
              >
                <IconButton
                  size="small"
                  aria-label={rebased ? "Turn off rebase" : "Rebase lines to zero at window start"}
                  onClick={() => setRebased((v) => !v)}
                  sx={{
                    p: 0.5,
                    color: rebased ? "primary.main" : "text.secondary",
                    "&:hover": { color: "primary.main" }
                  }}
                >
                  <AlignHorizontalLeftRounded sx={{ fontSize: 24 }} />
                </IconButton>
              </Tooltip>
            )}
            {/* Graph Settings — opens a dialog with switches for the
             *  SPY / Target / SPY+D comparison lines plus the
             *  Percent/Dollar view toggle. Replaces the inline $/%
             *  ToggleButtonGroup that used to live here; more room
             *  for future per-graph settings without crowding the
             *  range chip row. */}
            <Tooltip arrow placement="top" title="Graph settings">
              <IconButton
                size="small"
                aria-label="Open graph settings"
                onClick={() => setSettingsOpen(true)}
                sx={{
                  p: 0.5,
                  color: "text.secondary",
                  "&:hover": { color: "primary.main" }
                }}
              >
                <SettingsOutlined sx={{ fontSize: 24 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      ) : (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{
            mt: 1.5,
            justifyContent: "center",
            flexWrap: "wrap",
            rowGap: 0.5
          }}
        >
          {PRESET_RANGES.map((k) => (
            <Chip
              key={k}
              label={k === "ALL" ? "All" : k}
              size="small"
              onClick={() => {
                setRange(k);
                // Same auto-rebase policy as V2: non-ALL → ON, ALL → OFF.
                if (k === "ALL") setRebased(false);
                else setRebased(true);
                setLockedIdx(null);
              }}
              variant={range === k ? "filled" : "outlined"}
              color={range === k ? "primary" : "default"}
              sx={[activeRangeChipSx, range === k && activeRangeChipActiveSx]}
            />
          ))}
          {customRange && (
            <Chip
              label={formatCustomRangeLabel(customRange)}
              size="small"
              onClick={() => {
                setRange("CUSTOM");
                // Custom range is always windowed → rebase ON.
                setRebased(true);
                setLockedIdx(null);
              }}
              onDelete={() => {
                setCustomRange(null);
                if (range === "CUSTOM") setRange("ALL");
              }}
              variant={range === "CUSTOM" ? "filled" : "outlined"}
              color={range === "CUSTOM" ? "primary" : "default"}
              sx={[activeRangeChipSx, range === "CUSTOM" && activeRangeChipActiveSx]}
            />
          )}
          <IconButton
            size="small"
            aria-label="Pick custom date range"
            onClick={() => setPickerOpen(true)}
            sx={{
              p: 0.25,
              ml: 0.25,
              color: range === "CUSTOM" ? "primary.main" : "text.secondary",
              "&:hover": { color: "primary.main" }
            }}
          >
            <CalendarMonthRounded sx={{ fontSize: 18 }} />
          </IconButton>
          {!isUsd && (
            <Tooltip
              arrow
              placement="top"
              title={
                rebased
                  ? "Rebase is ON. Portfolio and SPY have been shifted so they both start at 0% at the left edge. Click to turn off and show actual percent change since the beginning of the chart's history."
                  : "Rebase: shift Portfolio and SPY so they both start at 0% at the left edge of the window. Makes it easier to compare how each performed within this window on equal footing. The Target line already starts at 0%, so it doesn't change."
              }
            >
              <IconButton
                size="small"
                aria-label={rebased ? "Turn off rebase" : "Rebase lines to 0% at window start"}
                onClick={() => setRebased((v) => !v)}
                sx={{
                  p: 0.25,
                  ml: 0.25,
                  color: rebased ? "primary.main" : "text.secondary",
                  "&:hover": { color: "primary.main" }
                }}
              >
                <AlignHorizontalLeftRounded sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )}
      <DateRangePickerDialog
        open={pickerOpen}
        minDate={account.curveDates[0] ?? ""}
        maxDate={account.curveDates[account.curveDates.length - 1] ?? ""}
        initial={customRange ?? {
          from: account.curveDates[0] ?? "",
          to: account.curveDates[account.curveDates.length - 1] ?? ""
        }}
        onClose={() => setPickerOpen(false)}
        onApply={(r) => {
          setCustomRange(r);
          setRange("CUSTOM");
          // Calendar-picker selections always want window-rebased view
          // (per Sergey Apr-2026: "selecting date range via calendar
          // picker will enable rebase by default").
          setRebased(true);
          setPickerOpen(false);
        }}
      />
      <TargetApyDialog
        open={targetDialogOpen}
        current={targetPct}
        accountDefault={account.targetApyPct ?? 20}
        hasOverride={targetPctOverride !== null}
        onClose={() => setTargetDialogOpen(false)}
        onApply={(pct) => {
          setTargetPctOverride(+pct.toFixed(2));
          setTargetDialogOpen(false);
        }}
        onReset={() => {
          setTargetPctOverride(null);
          setTargetDialogOpen(false);
        }}
      />
      <SmaWindowDialog
        open={smaDialogOpen}
        autoWindow={resolveSmaWindow("auto", smaWindowDays, smaRangeKey, customWindowDays, accountLifespanDays)}
        currentMode={smaWindowMode}
        currentDays={smaWindowDays}
        onClose={() => setSmaDialogOpen(false)}
        onApply={(mode, days) => {
          setSmaWindow(mode, days);
          setSmaDialogOpen(false);
        }}
      />
      {v2 && (
        <Dialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ pb: 1 }}>Graph settings</DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{ color: "text.secondary", letterSpacing: 0.6, fontWeight: 700 }}
                >
                  Chart type
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  value={mode}
                  onChange={(_, v) => { if (v) setMode(v as Mode); }}
                  sx={{ mt: 0.75, width: "100%" }}
                  size="small"
                >
                  <ToggleButton value="pct" sx={{ flex: 1, fontWeight: 700 }}>
                    Percent (%)
                  </ToggleButton>
                  <ToggleButton value="usd" sx={{ flex: 1, fontWeight: 700 }}>
                    Dollar ($)
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography
                  variant="caption"
                  sx={{ mt: 0.5, display: "block", color: "text.secondary" }}
                >
                  Tip: tap the +% or +$ chip above the graph to switch quickly.
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography
                  variant="overline"
                  sx={{ color: "text.secondary", letterSpacing: 0.6, fontWeight: 700 }}
                >
                  Display
                </Typography>
                <Stack sx={{ mt: 0.25 }}>
                  {/* R12-11 "Selected Range" — when ON, the centered
                   *  date-label slot below the chip row shows the
                   *  active range ("Past year" / "All time" / custom
                   *  dates) while the user is NOT hovering. Hovering a
                   *  specific date always wins. Off by default — the
                   *  slot is empty at rest unless the user opts in. */}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showSelectedRange}
                        onChange={(e) => setShowSelectedRange(e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>
                          Selected Range
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Show the active range above the chart when not hovering
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: "flex-start", m: 0, py: 0.5 }}
                  />
                </Stack>
              </Box>
              <Divider />
              <Box>
                <Typography
                  variant="overline"
                  sx={{ color: "text.secondary", letterSpacing: 0.6, fontWeight: 700 }}
                >
                  Comparison lines
                </Typography>
                <Stack sx={{ mt: 0.25 }}>
                  {/* Each switch here controls chip+line existence. When
                   *  flipped OFF the chip disappears from the hero strip
                   *  and the corresponding line is removed. Clicking the
                   *  chip itself (while enabled) only toggles the line —
                   *  the chip stays visible, dimmed. */}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enabledSpy}
                        onChange={(e) => setEnabled("spy", e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>
                          SPY
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          S&amp;P 500 price benchmark
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: "flex-start", m: 0, py: 0.5 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enabledSpyDiv}
                        onChange={(e) => setEnabled("spyDiv", e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>
                          SPY + Dividends
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Price plus received SPY dividends reinvested
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: "flex-start", m: 0, py: 0.5 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enabledTarget}
                        onChange={(e) => setEnabled("target", e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>
                          Target ({targetPct.toFixed(0)}%)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Your APY goal line
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: "flex-start", m: 0, py: 0.5 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enabledCashflow}
                        onChange={(e) => setEnabled("cashflow", e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>
                          Cashflows
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Deposit (cyan) and withdrawal (red) dots on the line
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: "flex-start", m: 0, py: 0.5 }}
                  />
                  {isUsd && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={enabledNetDeposits}
                          onChange={(e) => setEnabled("netDeposits", e.target.checked)}
                        />
                      }
                      label={
                        <Box>
                          <Typography sx={{ fontWeight: 600 }}>
                            Net Deposits
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Stepped capital-held line: jumps on each deposit / withdrawal
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: "flex-start", m: 0, py: 0.5 }}
                    />
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enabledSma}
                        onChange={(e) => setEnabled("sma", e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>
                          SMA
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {smaWindowMode === "auto"
                            ? `Trailing simple moving average · auto-pick by range (currently ${smaWindow > 0 ? `${smaWindow}d` : "Off"})`
                            : `Trailing simple moving average · fixed at ${smaWindowDays}d`}
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: "flex-start", m: 0, py: 0.5 }}
                  />
                  {/* Master toggle — flips every comparison-line switch
                   *  visible in the current mode ON or OFF together. Acts
                   *  on the `enabled*` (chip-visibility) flags, so turning
                   *  all off removes every chip from the hero strip. The
                   *  store's `setAll` skips ND in % mode automatically. */}
                  {(() => {
                    const flags = isUsd
                      ? [enabledSpy, enabledSpyDiv, enabledTarget, enabledCashflow, enabledNetDeposits, enabledSma]
                      : [enabledSpy, enabledSpyDiv, enabledTarget, enabledCashflow, enabledSma];
                    const allOn = flags.every(Boolean);
                    const handleToggle = (checked: boolean) => {
                      setAllInStore(chartMode, checked);
                    };
                    return (
                      <>
                        <Divider sx={{ my: 0.5 }} />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={allOn}
                              onChange={(e) => handleToggle(e.target.checked)}
                            />
                          }
                          label={
                            <Typography sx={{ fontWeight: 700 }}>
                              {allOn ? "Disable All" : "Enable All"}
                            </Typography>
                          }
                          sx={{ alignItems: "center", m: 0, py: 0.5 }}
                        />
                      </>
                    );
                  })()}
                </Stack>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setSettingsOpen(false)}
              sx={{ fontSize: "1rem", fontWeight: 700, textTransform: "none" }}
            >
              Done
            </Button>
          </DialogActions>
        </Dialog>
      )}
      </>}
    </Card>
  );
}

/* ───────────────────────── internals ───────────────────────── */

/** "Apr 3 – Jul 17" style label for the custom-range chip. */
function formatCustomRangeLabel(r: CustomRange): string {
  const parse = (iso: string) => {
    const [y, m, d] = iso.split("-").map((s) => parseInt(s, 10));
    return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
  };
  const from = parse(r.from);
  const to = parse(r.to);
  const fmt = (d: Date) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${fmt(from)} – ${fmt(to)}`;
}

/**
 * Custom date-range picker Dialog.
 *
 * We use native `<input type="date">` (wrapped in TextField) rather
 * than MUI X Date Pickers because `@mui/x-date-pickers` isn't a
 * dependency — and the native widget is more than enough for picking
 * a chart window. `min`/`max` attributes constrain selection to the
 * account's actual curve window so users can't pick a range with no
 * data.
 */
function DateRangePickerDialog({
  open, onClose, onApply, initial, minDate, maxDate
}: {
  open: boolean;
  onClose: () => void;
  onApply: (r: CustomRange) => void;
  initial: CustomRange;
  minDate: string;
  maxDate: string;
}) {
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);

  // Re-sync the inputs to `initial` each time the dialog opens. Without
  // this, the second-and-later opens keep the user's last (possibly
  // cancelled) edits instead of showing the currently applied range.
  useEffect(() => {
    if (open) {
      setFrom(initial.from);
      setTo(initial.to);
    }
  }, [open, initial.from, initial.to]);

  const valid =
    !!from && !!to && from <= to &&
    (!minDate || from >= minDate) &&
    (!maxDate || to <= maxDate);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Custom date range</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            type="date"
            label="From"
            size="small"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            slotProps={{
              htmlInput: { min: minDate || undefined, max: maxDate || undefined },
              inputLabel: { shrink: true }
            }}
            fullWidth
          />
          <TextField
            type="date"
            label="To"
            size="small"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            slotProps={{
              htmlInput: { min: minDate || undefined, max: maxDate || undefined },
              inputLabel: { shrink: true }
            }}
            fullWidth
          />
          {!valid && from && to && (
            <Typography variant="caption" color="error">
              Pick a valid range inside {minDate} – {maxDate}.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!valid}
          onClick={() => onApply({ from, to })}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Target APY editor Dialog.
 *
 * Replaces the browser-native `window.prompt` that used to open when
 * the user clicked the gear on the Target chip. A plain numeric
 * TextField (step 0.5, min 0.1, max 500) lets them tune the annual
 * target; Enter submits, Escape cancels. A "Reset to account default"
 * button appears when there's a local override so users can get back
 * to the canonical value without remembering it.
 *
 * The field is wrapped so Enter inside the input fires `handleApply`
 * without needing to click Apply — matches the feel of the browser
 * prompt it's replacing, just with real Material styling.
 */
/**
 * Window picker for the SMA chip — opened from the gear icon on the
 * SMA chip. Three modes:
 *
 *   • Auto      — let the chart pick by range (W=Off, 1M=5d, …, ALL=50/100d).
 *                 The currently-active auto window is shown in the
 *                 row label so the user knows what "Auto" gives them
 *                 right now.
 *   • Preset    — fixed at 5 / 10 / 20 / 30 / 50 / 100 / 200 days.
 *                 Same window across every range.
 *   • Custom    — any integer 2–500. Shows a number input; Enter
 *                 applies. Useful for "I always want the 14-day".
 *
 * Lives in the chart-settings store under `smaWindowMode` /
 * `smaWindowDays` so user choices persist across reloads. Sergey
 * 2026-04-30.
 */
function SmaWindowDialog({
  open,
  autoWindow,
  currentMode,
  currentDays,
  onClose,
  onApply
}: {
  open: boolean;
  /** Window length the auto rule would pick right now. Displayed
   *  next to the "Auto" radio so the user knows what they'd get. */
  autoWindow: number;
  currentMode: "auto" | "fixed";
  currentDays: number;
  onClose: () => void;
  onApply: (mode: "auto" | "fixed", days?: number) => void;
}) {
  // Local state so the user can flip between options and tweak Custom
  // before applying. Synced from the store on every (re)open.
  const [mode, setMode] = useState<"auto" | "fixed">(currentMode);
  const [days, setDays] = useState<number>(currentDays);
  const [customRaw, setCustomRaw] = useState<string>(String(currentDays));
  const isPreset = SMA_PRESETS.includes(days);

  useEffect(() => {
    if (!open) return;
    setMode(currentMode);
    setDays(currentDays);
    setCustomRaw(String(currentDays));
  }, [open, currentMode, currentDays]);

  const customParsed = parseInt(customRaw, 10);
  const customValid =
    Number.isFinite(customParsed) && customParsed >= 2 && customParsed <= 500;
  const customSelected = mode === "fixed" && !isPreset;

  const handleApply = () => {
    if (mode === "auto") {
      onApply("auto");
      return;
    }
    if (customSelected) {
      if (customValid) onApply("fixed", customParsed);
      return;
    }
    onApply("fixed", days);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>SMA window</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Trailing simple moving average over the Portfolio line. Auto picks
          the window based on the chart's range; pick a fixed number for the
          same smoothing across every range.
        </Typography>
        <Stack spacing={0.5} sx={{ mb: 1 }}>
          <Box
            role="button"
            onClick={() => setMode("auto")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1,
              py: 1,
              borderRadius: 1.5,
              cursor: "pointer",
              bgcolor: mode === "auto" ? "action.selected" : "transparent",
              "&:hover": { bgcolor: "action.hover" }
            }}
          >
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                border: 2,
                borderColor: mode === "auto" ? "primary.main" : "divider",
                position: "relative",
                "&::after":
                  mode === "auto"
                    ? {
                        content: '""',
                        position: "absolute",
                        inset: 3,
                        borderRadius: "50%",
                        bgcolor: "primary.main"
                      }
                    : {}
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Auto
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {autoWindow >= 2 ? `currently ${autoWindow}d` : "Off for this range"}
            </Typography>
          </Box>
          {SMA_PRESETS.map((p) => (
            <Box
              key={p}
              role="button"
              onClick={() => {
                setMode("fixed");
                setDays(p);
                setCustomRaw(String(p));
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1,
                py: 1,
                borderRadius: 1.5,
                cursor: "pointer",
                bgcolor:
                  mode === "fixed" && days === p
                    ? "action.selected"
                    : "transparent",
                "&:hover": { bgcolor: "action.hover" }
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  border: 2,
                  borderColor:
                    mode === "fixed" && days === p ? "primary.main" : "divider",
                  position: "relative",
                  "&::after":
                    mode === "fixed" && days === p
                      ? {
                          content: '""',
                          position: "absolute",
                          inset: 3,
                          borderRadius: "50%",
                          bgcolor: "primary.main"
                        }
                      : {}
                }}
              />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {p} days
              </Typography>
            </Box>
          ))}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1,
              py: 1,
              borderRadius: 1.5,
              bgcolor: customSelected ? "action.selected" : "transparent"
            }}
          >
            <Box
              role="button"
              onClick={() => {
                setMode("fixed");
                if (customValid) setDays(customParsed);
              }}
              sx={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                border: 2,
                borderColor: customSelected ? "primary.main" : "divider",
                position: "relative",
                cursor: "pointer",
                "&::after": customSelected
                  ? {
                      content: '""',
                      position: "absolute",
                      inset: 3,
                      borderRadius: "50%",
                      bgcolor: "primary.main"
                    }
                  : {}
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 500, mr: 1 }}>
              Custom
            </Typography>
            <TextField
              size="small"
              type="number"
              value={customRaw}
              onChange={(e) => {
                setCustomRaw(e.target.value);
                const v = parseInt(e.target.value, 10);
                if (Number.isFinite(v) && v >= 2 && v <= 500) {
                  setMode("fixed");
                  setDays(v);
                }
              }}
              onFocus={() => setMode("fixed")}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleApply();
                }
              }}
              slotProps={{
                htmlInput: {
                  min: 2,
                  max: 500,
                  step: 1,
                  inputMode: "numeric",
                  "aria-label": "Custom SMA window in days"
                }
              }}
              error={customSelected && !customValid}
              sx={{ width: 96 }}
            />
            <Typography variant="caption" color="text.secondary">
              days (2–500)
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={mode === "fixed" && customSelected && !customValid}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TargetApyDialog({
  open, current, accountDefault, hasOverride, onClose, onApply, onReset
}: {
  open: boolean;
  current: number;
  accountDefault: number;
  hasOverride: boolean;
  onClose: () => void;
  onApply: (pct: number) => void;
  onReset: () => void;
}) {
  // Store the input as a string so the user can clear it / type "12."
  // mid-edit without the field snapping back. We parse on submit.
  const [raw, setRaw] = useState(String(current));

  // Re-sync when the dialog is (re)opened so the field always reflects
  // the current value of the Target line.
  useEffect(() => {
    if (open) setRaw(String(current));
  }, [open, current]);

  const parsed = parseFloat(raw);
  const valid = Number.isFinite(parsed) && parsed > 0 && parsed <= 500;

  const handleApply = () => {
    if (valid) onApply(parsed);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Edit Target APY</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Annual compounding return target for the Target line on the chart.
          {accountDefault != null &&
            ` The account default is ${accountDefault}%.`}
        </Typography>
        <TextField
          type="number"
          label="Target APY"
          size="small"
          autoFocus
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleApply();
            }
          }}
          error={!!raw && !valid}
          helperText={
            !!raw && !valid
              ? "Enter a number between 0.1 and 500."
              : " " /* reserve row so layout doesn't jump */
          }
          slotProps={{
            htmlInput: { min: 0.1, max: 500, step: 0.5, inputMode: "decimal" },
            input: { endAdornment: (
              <Typography component="span" color="text.secondary" sx={{ pl: 0.5 }}>
                %
              </Typography>
            ) },
            inputLabel: { shrink: true }
          }}
          fullWidth
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
        {/* Reset button only appears when there's a local override —
         *  otherwise it'd be a no-op and just adds visual noise. */}
        <Box sx={{ flex: 1 }}>
          {hasOverride && (
            <Button onClick={onReset} size="small" color="inherit">
              Reset to account default
            </Button>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" disabled={!valid} onClick={handleApply}>
            Apply
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Hollow "donut" marker drawn at each line ↔ crosshair intersection.
 *
 * MUI X Charts' default `LineHighlightElement` renders a solid `<circle
 * fill={seriesColor}/>` with no stroke, so a pure CSS override can
 * blank the fill but can't colour the stroke (stroke isn't set in the
 * first place, and we can't read the series colour from CSS anyway).
 *
 * This slot receives the series `color` prop and renders a proper
 * donut: background-coloured fill + coloured stroke. Result: the
 * hover dot is visually tied to its line even when the series palette
 * changes.
 */
function HollowHighlight(props: {
  x?: number;
  y?: number;
  color?: string;
  paperColor: string;
  classes?: { root?: string };
}) {
  const { x, y, color, paperColor, classes } = props;
  if (x == null || y == null) return null;
  return (
    <circle
      cx={x}
      cy={y}
      r={5}
      fill={paperColor}
      stroke={color ?? "currentColor"}
      strokeWidth={2}
      className={classes?.root}
    />
  );
}

/**
 * HoverTracker — zero-visual-output component.
 *
 * Lives *inside* `ResponsiveChartContainer` (so it can use the context
 * hook `useAxisTooltip()`), and pipes the current x-axis index back up
 * to the parent component via the `onChange` callback. This replaces
 * the popper-rendered `<ChartsTooltip>` from earlier rounds — chip
 * values + the centered date label above are now the "tooltip".
 *
 * `onChange` is expected to be a stable setter (e.g. React's
 * `setState` setter). We fire it in an effect so we can pass `null`
 * when the user moves off the chart.
 */
function HoverTracker({ onChange }: { onChange: (idx: number | null) => void }) {
  const tip = useAxisTooltip();
  const idx = tip?.identifier.x?.index ?? null;
  useEffect(() => {
    onChange(idx);
  }, [idx, onChange]);
  return null;
}

function LegendChip({
  color, secondaryColor, label, value, valuePositive, active = true, onClick,
  actionIcon, actionLabel, onAction, term, size = "sm"
}: {
  color: string;
  /** Optional second color for a split-dot (diagonal two-tone) indicator.
   *  Used by the Cashflow chip to show both deposit (cyan) and
   *  withdrawal (red) in a single legend dot. */
  secondaryColor?: string;
  label: string;
  /** Optional trailing numeric value. Omit for label-only chips (e.g. Target). */
  value?: string;
  valuePositive?: boolean;
  active?: boolean;
  onClick?: () => void;
  /** Optional icon rendered inside the chip as a nested action button
   *  (e.g. the gear icon on the Target chip). Clicking it stops
   *  propagation so it doesn't also toggle `onClick`. */
  actionIcon?: ReactNode;
  actionLabel?: string;
  onAction?: () => void;
  term: { title: string; body: string };
  /** "sm" = 0.75rem label (V1 default). "md" = 1rem (body1) to match
   *  the Buying Power row in V2 — Apr-2026 feedback wanted SPY / T chip
   *  text to read at the same visual weight as the rest of the hero. */
  size?: "sm" | "md";
}) {
  const labelFontSize = size === "md" ? "1rem" : "0.75rem";
  // Outer element is always a `div` — we use role="button" for the
  // clickable chip so we can still nest a native <button> inside for
  // the action icon. Nested native <button>s are invalid HTML and some
  // browsers drop the inner click, so div+role is the safe choice.
  return (
    <MetricTip term={term}>
      <Box
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        onClick={onClick}
        onKeyDown={onClick ? (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        } : undefined}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.5,
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          cursor: onClick ? "pointer" : "help",
          opacity: active ? 1 : 0.45,
          fontFamily: "inherit",
          color: "inherit",
          whiteSpace: "nowrap",
          flexShrink: 0,
          transition: "opacity .15s",
          outline: "none",
          "&:focus-visible": {
            boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}`
          }
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            flexShrink: 0,
            ...(secondaryColor
              ? {
                  // Diagonal split dot — half color, half secondaryColor.
                  // Used by the Cashflow chip to show deposits (cyan)
                  // + withdrawals (red) in one indicator.
                  background: `linear-gradient(135deg, ${color} 0 50%, ${secondaryColor} 50% 100%)`
                }
              : { bgcolor: color })
          }}
        />
        <Typography component="span" sx={{ fontWeight: 600, fontSize: labelFontSize }}>
          {label}
        </Typography>
        {value !== undefined && (
          <Typography
            component="span"
            sx={{
              fontWeight: 700,
              fontSize: labelFontSize,
              color: valuePositive ? "primary.main" : "error.main",
              fontFeatureSettings: '"tnum" 1'
            }}
          >
            {value}
          </Typography>
        )}
        {actionIcon && (
          <IconButton
            size="small"
            aria-label={actionLabel ?? "Chip action"}
            onClick={(e) => {
              e.stopPropagation();
              onAction?.();
            }}
            sx={{
              p: 0.25,
              ml: 0.25,
              color: "text.secondary",
              "&:hover": { color: "text.primary" }
            }}
          >
            {actionIcon}
          </IconButton>
        )}
      </Box>
    </MetricTip>
  );
}

// Re-export unused plots so tree-shaking doesn't drop them if a caller
// wants the bare chart. (No effect at runtime.)
export { LineChart, ScatterChart };
